import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ArrowUpRight, Sparkles, Hash, LineChart, CalendarDays, Zap, Heart,
  MessageSquare, Swords, Gauge, Users, Globe, Film, TrendingUp, UserCheck,
  CheckCircle2, Rocket, MousePointerClick, Tag, ShoppingBag, Share2, Palette,
  Clock, Search, Handshake, DollarSign, Star, Send, Target, BarChart3,
  Play, PhoneCall, MapPin, HelpCircle, Instagram, Youtube, Ticket, Wallet,
  Megaphone, MessageCircle, Music2, Ghost, Linkedin, Briefcase, FileText,
  Calendar, Mail, MoreHorizontal, Loader2, QrCode, Eye, Coins, Repeat, X,
  Filter, Stethoscope, BedDouble, Utensils, Coffee, Dumbbell, Shirt, Mic,
  Music, User, Radius, MapPinned, Store,
} from "lucide-react";
import LinkolaMark from "../components/LinkolaMark";
import AppStoreBadges from "../components/AppStoreBadges";
import ChannelPartnerModal from "../components/ChannelPartnerModal";
import ContactSalesModal from "../components/ContactSalesModal";
import BusinessLanding, { BusinessNav } from "../components/landing/BusinessLanding";
import { BUSINESS_SECTIONS, DEFAULT_SECTIONS } from "../lib/landingSections";
import { api } from "../lib/api";
import { trackSiteEvent } from "../lib/siteTrack";
import { externalLinkClickHandler } from "../lib/openExternal";
import { toast } from "sonner";

// v145.2 — Hero icon rail: 15 attractive rounded cards showing what you
// can plug into your Linkola bio page. Colour palette leans playful — mint
// / lavender / sun / coral / bubble / etc — so the grid feels alive rather
// than corporate. Icons are lucide-react (already bundled).
// v145.4 — Spotify gets its own official brand green (#1DB954) as a solid
// swatch so it visually separates from WhatsApp's emerald/teal gradient.
// Every card also renders a small always-visible label below (no hover
// needed) so the whole rail reads at a glance on mobile.
const HERO_ICONS = [
  { label: "Instagram",   icon: Instagram,       bg: "bg-gradient-to-br from-pink-400 to-fuchsia-500",     fg: "text-white" },
  { label: "WhatsApp",    icon: MessageCircle,   bg: "bg-gradient-to-br from-emerald-400 to-teal-500",     fg: "text-white" },
  { label: "YouTube",     icon: Youtube,         bg: "bg-gradient-to-br from-red-500 to-rose-500",         fg: "text-white" },
  { label: "Spotify",     icon: Music2,          bg: "bg-[#1DB954]",                                        fg: "text-white" },
  { label: "Snapchat",    icon: Ghost,           bg: "bg-[#FFFC00]",                                        fg: "text-ink" },
  { label: "LinkedIn",    icon: Linkedin,        bg: "bg-[#0A66C2]",                                        fg: "text-white" },
  { label: "Website",     icon: Globe,           bg: "bg-gradient-to-br from-indigo-400 to-purple-500",    fg: "text-white" },
  { label: "Portfolio",   icon: Briefcase,       bg: "bg-mint",                                            fg: "text-ink" },
  { label: "Resume",      icon: FileText,        bg: "bg-lavender",                                        fg: "text-ink" },
  { label: "Products",    icon: ShoppingBag,     bg: "bg-sun",                                             fg: "text-ink" },
  { label: "Booking",     icon: Calendar,        bg: "bg-bubble",                                          fg: "text-ink" },
  { label: "Enquiry",     icon: HelpCircle,      bg: "bg-gradient-to-br from-orange-300 to-red-300",       fg: "text-ink" },
  { label: "Maps",        icon: MapPin,          bg: "bg-gradient-to-br from-teal-300 to-cyan-400",        fg: "text-ink" },
  { label: "Email",       icon: Mail,            bg: "bg-gradient-to-br from-purple-400 to-fuchsia-400",   fg: "text-white" },
  { label: "More",        icon: MoreHorizontal,  bg: "bg-white",                                           fg: "text-ink" },
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// v149.62 — Section keys + defaults live in `lib/landingSections.js`
// (shared with Admin → Landing). The business-outcome sections render
// first (`components/landing/BusinessLanding.jsx`); the classic ones below
// are hidden by default and each can be switched back on.

// v149.64 — The section toggles this browser last received, so a repeat
// visit draws the right layout before `/landing/config` answers.
const SECTIONS_KEY = "lk_landing_sections_v2";
function readRememberedSections() {
  try {
    const raw = localStorage.getItem(SECTIONS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch { return {}; }
}
function rememberSections(sections) {
  try { localStorage.setItem(SECTIONS_KEY, JSON.stringify(sections || {})); } catch { /* no storage */ }
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // v147.26 — When a signed-in user hits the marketing landing (either
  // by typing `/`, tapping the app icon on native, or after a redirect
  // that lost the intended /app destination), bounce them straight to
  // their Dashboard. Prevents the "why did it kick me back to landing?"
  // complaint on both web and the Capacitor native app.
  useEffect(() => {
    if (user) {
      navigate("/app", { replace: true });
    }
  }, [user, navigate]);
  const [plans, setPlans] = useState([]);
  // v147.20 — Monthly / Yearly toggle for the Action Hub pricing grid.
  // Filters plans by billing_cycle so users see one card per tier
  // matching their preferred cadence.
  const [pricingCycle, setPricingCycle] = useState("yearly");
  // v149.64 — Draw the page at once. The layout this browser saw last
  // time is used until the admin's toggles arrive (a per-viewer
  // convenience, never the source of truth); a first visit uses the
  // defaults. Waiting on `/landing/config` left the page blank for as long
  // as that call took — 1–2 s or more through the origin.
  const [config, setConfig] = useState(() => ({ sections: { ...DEFAULT_SECTIONS, ...readRememberedSections() } }));
  const [trending, setTrending] = useState([]);
  // v146.41 — Public reads for the landing page enhancements.
  const [qrPricing, setQrPricing] = useState(null);
  // v148.x — Channel Partner CTA modal state. Shared across the 3
  // landing-page entry points (header, pricing card, footer).
  const [cpModalOpen, setCpModalOpen] = useState(false);
  const [cpSource, setCpSource] = useState("landing_header");
  // v149.62 — "Talk to us" (multi-branch) opens the Corporate lead form.
  const [salesOpen, setSalesOpen] = useState(false);
  const sections = config.sections;
  const businessOn = BUSINESS_SECTIONS.some((s) => sections[s.key]);

  // v149.64 — Only the sections on the page fetch what they show: the QR
  // pricing card (Smart QR explainer), the plan grid (Pricing) and the
  // Trending grid. With the business homepage on, none of the three is,
  // and the landing page makes three fewer requests through the origin.
  const wantQrPricing = sections.smart_qr_explainer !== false;
  const wantPlans = !!sections.pricing;
  const wantTrending = !!sections.trending_pages;
  useEffect(() => {
    if (!wantQrPricing) return;
    api.get("/smart-qr/apply-settings")
      .then(({ data }) => setQrPricing(data))
      .catch(() => setQrPricing(null));
  }, [wantQrPricing]);
  useEffect(() => {
    if (!wantPlans) return;
    api.get("/plans").then(({ data }) => setPlans(Array.isArray(data) ? data : [])).catch(() => setPlans([]));
  }, [wantPlans]);
  useEffect(() => {
    if (!wantTrending) return;
    api.get("/trending/pages?limit=8").then(({ data }) => setTrending(Array.isArray(data?.pages) ? data.pages : [])).catch(() => setTrending([]));
  }, [wantTrending]);
  useEffect(() => {
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("ref");
    if (code && code.length <= 32) {
      localStorage.setItem("ref_code", code.trim().toUpperCase());
    }
    // v145.1 — Log the landing-page view for Super Admin analytics.
    // Fire once per page-load; dedupe is enforced inside trackSiteEvent.
    trackSiteEvent("landing_view", {
      ref: params.get("ref") || null,
      utm_source: params.get("utm_source") || null,
      utm_campaign: params.get("utm_campaign") || null,
    });
    // Auto-scroll to #signin if the URL has a signin hash
    if (window.location.hash === "#signin") {
      setTimeout(() => document.getElementById("signin")?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    }
  }, []);

  // v138.2 — After signing in from a demo hub's "Use this as my starting
  // point" ribbon, we land back here with `?clone_after_signin=<slug>`.
  // v138.3 — Instead of auto-cloning silently, bounce the user BACK to
  // the demo hub so they see the ClonePreviewModal + confirm the choice.
  // The stashed sessionStorage entry is preserved so the demo page can
  // auto-open its preview modal on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wantClone = params.get("clone_after_signin");
    if (!wantClone || !user) return;
    let pending = null;
    try {
      pending = JSON.parse(sessionStorage.getItem("pending_demo_clone") || "null");
    } catch { /* ignore */ }
    if (!pending?.source_slug) return;
    // Send them back to the demo hub — the preview modal auto-opens there.
    navigate(`/u/${encodeURIComponent(pending.source_slug)}?open_clone_preview=1`, { replace: true });
  }, [user, navigate]);

  // The admin's section toggles.
  useEffect(() => {
    api.get("/landing/config").then(({ data }) => {
      // Always merge incoming sections on top of local defaults so a stale
      // DB record missing newly-added keys never crashes the render below.
      const incoming = (data && data.sections) || {};
      setConfig({ ...(data || {}), sections: { ...DEFAULT_SECTIONS, ...incoming } });
      rememberSections(incoming);
    }).catch(() => {});
  }, []);

  const signIn = (arg) => {
    // v146.10 — Auth is now on its own page (/signin) — no more
    // scroll-to-anchor on the landing hero. Callers can pass a string
    // "login" to force the sign-in tab; most call sites just wire this
    // as an onClick handler (event arg — ignore it).
    // v146.35 — Fire `signup_click` when the CTA is going to /signup
    // (the default for every "Sign up" button on the landing page).
    // The old code always fired `signin_click`, which is why the Super
    // Admin analytics chart was always seeing 0 sign-up clicks even
    // though the "Sign up" button was the most-clicked CTA.
    const mode = typeof arg === "string" ? arg : undefined;
    const goingToSignup = mode !== "login";
    trackSiteEvent(goingToSignup ? "signup_click" : "signin_click", {
      path: window.location.pathname + window.location.hash,
      mode: mode || "signup",
    });
    if (user) { navigate("/app"); return; }
    navigate(goingToSignup ? "/signup" : "/signin?mode=login");
  };

  const goApp = () => {
    navigate("/app");
  };

  return (
    // v149.76 — `data-landing="v3"` scopes the homepage theme
    // (styles/landing-v3.css) to the business homepage: header, sections and
    // footer. The classic homepage (every biz_* off) keeps its old look.
    <div className="min-h-screen bg-page text-ink" data-landing={businessOn ? "v3" : undefined}>
      {/* Nav */}
      <header className="border-b-2 border-ink bg-page sticky top-0 z-30 pt-[env(safe-area-inset-top,0)]" data-landing-part="header">
        {businessOn ? (
          <BusinessNav
            sections={sections}
            user={user}
            onGetLinkola={() => signIn()}
            onLogin={() => signIn("login")}
            onOpenApp={goApp}
            onPartner={() => { setCpSource("landing_header"); setCpModalOpen(true); }}
          />
        ) : (
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <LinkolaMark size={40} />
            <span className="font-display font-black text-xl tracking-tighter">Linkola</span>
          </div>
          {/* v146.4 — Nav links are section-aware. When the admin
              toggles off Features / CTA Hub / Reels (and there's no
              How-it-works / Pricing either), the entire nav hides so
              we don't render dead anchors that scroll into empty space. */}
          {(() => {
            const showFeatures = sections.digital_insights !== false;
            const showCtaHub = sections.action_hub_features !== false;
            const showReels = sections.digital_insights !== false;
            const showHow = sections.how_it_works_action_hub || sections.how_it_works_di;
            const showPricing = sections.pricing !== false;
            const anyLink = showFeatures || showCtaHub || showReels || showHow || showPricing;
            if (!anyLink) return null;
            return (
              <nav className="hidden md:flex items-center gap-8" data-testid="landing-nav">
                {showFeatures && (
                  <a href="#features" className="font-semibold text-sm hover:underline underline-offset-4">Features</a>
                )}
                {showCtaHub && (
                  <a href="#action-hub" className="font-semibold text-sm hover:underline underline-offset-4">CTA Hub</a>
                )}
                {showReels && (
                  <a href="#reels" className="font-semibold text-sm hover:underline underline-offset-4">Reels</a>
                )}
                {showHow && (
                  <a href={sections.how_it_works_action_hub ? "#how-action-hub" : "#how"}
                     className="font-semibold text-sm hover:underline underline-offset-4">How it works</a>
                )}
                {showPricing && (
                  <a href="#pricing" className="font-semibold text-sm hover:underline underline-offset-4">Pricing</a>
                )}
              </nav>
            );
          })()}
          {user ? (
            <button data-testid="nav-go-app-btn" onClick={goApp} className="btn-primary">
              Open app <ArrowUpRight className="w-4 h-4" />
            </button>
          ) : (
            <>
              {/* v148.x — Partner CTA. Yellow pill so it visually pops
                  next to the coral Sign In without stealing the primary
                  CTA. Hidden on the smallest screens to keep the header
                  from wrapping — it's still available in the footer +
                  pricing card + More menu. */}
              <button
                data-testid="nav-partner-btn"
                onClick={() => { setCpSource("landing_header"); setCpModalOpen(true); }}
                className="inline-flex items-center gap-1 border-2 border-ink bg-sun px-2 sm:px-2.5 py-1.5 text-[11px] sm:text-sm font-black shadow-brutalSm whitespace-nowrap"
              >
                Partner
              </button>
              {/* v147.99.17 — Top-right nav button renamed "Sign up" → "Sign In"
                and routed to /signin?mode=login. Returning users kept
                tapping this looking for a login option and landed on the
                signup form instead. The hero "Create FREE Bio Page" and
                the footer "Sign up in 5 minutes" CTAs still route to
                /signup — the sign-in / sign-up toggle inside AuthPage
                lets visitors switch either way after landing there. */}
              <button data-testid="nav-signin-btn" onClick={() => signIn("login")} className="btn-primary">
                Sign In
              </button>
            </>
          )}
        </div>
        )}
      </header>

      {businessOn && (
        <BusinessLanding
          order={BUSINESS_SECTIONS.map((s) => s.key)}
          sections={sections}
          user={user}
          onGetLinkola={() => signIn()}
          onOpenApp={goApp}
          onTalk={() => setSalesOpen(true)}
        />
      )}

      {/* Hero — v145.2: FREE Personal Bio Page as the primary offer.
          Everything is optimized for the "3-second understand" test —
          badge, headline, one CTA, icon rail, personas, trust line. */}
      {sections.hero !== false && (
      <section className="relative border-b-2 border-ink overflow-hidden bg-page">
        {/* Ambient gradient blobs — desktop only, purely decorative */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-60">
          <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full blur-3xl bg-gradient-to-br from-fuchsia-400/40 via-purple-400/30 to-orange-300/30" />
          <div className="absolute top-40 -right-32 w-96 h-96 rounded-full blur-3xl bg-gradient-to-br from-orange-400/40 via-pink-300/30 to-purple-300/30" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 md:py-28">
          <div className="max-w-3xl mx-auto text-center">
            {/* FREE FOREVER badge */}
            <div
              className="inline-flex items-center gap-2 border-2 border-ink bg-gradient-to-r from-emerald-400 to-teal-400 text-ink px-4 py-1.5 mb-6 shadow-brutalSm animate-hero-fade"
              data-testid="hero-free-badge"
            >
              <span className="w-2 h-2 bg-ink rounded-full animate-pulse" />
              <span className="overline text-[10px] sm:text-[11px]">
                FREE FOREVER · Personal Web Links
              </span>
            </div>

            {/* Headline */}
            <h1
              className="font-display font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] animate-hero-fade"
              data-testid="hero-headline"
              style={{ animationDelay: "60ms" }}
            >
              Create Your{" "}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-purple-600 via-fuchsia-500 to-orange-400 bg-clip-text text-transparent">
                  FREE
                </span>
              </span>{" "}
              Personal
              <br className="hidden sm:block" />{" "}
              Web Link.
            </h1>

            {/* Sub heading */}
            <p
              className="mt-6 text-base sm:text-lg text-ink/75 max-w-2xl mx-auto leading-relaxed animate-hero-fade"
              data-testid="hero-tagline"
              style={{ animationDelay: "120ms" }}
            >
              One Smart Link for everything about you.<br className="hidden sm:block" />{" "}
              Share your social profiles, videos, portfolio, resume, products, bookings and more — from one beautiful page.
            </p>

            {/* v149.80 — Realistic venue photo strip. Three shop interiors
                with Linkola screens glowing inside; mobile-first (3 cols
                at every width) and sits above the icon rail so visitors
                see "where does Linkola live" before scrolling. Brutal
                border + hard shadow preserved; conic halo + ring are
                purely decorative. */}
            <div
              className="mt-8 sm:mt-10 relative animate-hero-fade"
              style={{ animationDelay: "150ms" }}
              data-testid="hero-shop-strip"
            >
              <div
                aria-hidden
                className="absolute inset-0 rounded-2xl blur-2xl opacity-70 pointer-events-none"
                style={{
                  zIndex: 0,
                  background:
                    "conic-gradient(from 160deg at 50% 50%, rgba(236,72,153,0.35), rgba(251,146,60,0.35), rgba(16,185,129,0.35), rgba(167,139,250,0.35), rgba(236,72,153,0.35))",
                }}
              />
              <div className="relative grid grid-cols-3 gap-2 sm:gap-3">
                {[
                  { src: "/landing/hero-shop-screen.webp", alt: "Linkola Smart Screen glowing inside a modern cafe", caption: "Cafe · scan to earn" },
                  { src: "/landing/reception-screen.webp", alt: "Linkola screen on a premium reception counter", caption: "Reception · live offers" },
                  { src: "/landing/venue-salon.webp", alt: "Linkola screen in a salon running local offers", caption: "Salon · loyalty" },
                ].map((img, i) => (
                  <figure
                    key={img.src}
                    data-testid={`hero-shop-photo-${i}`}
                    className="relative aspect-[4/5] sm:aspect-[3/4] overflow-hidden border-2 border-ink shadow-brutalSm hover:shadow-brutal hover:-translate-y-0.5 transition-all bg-ink"
                  >
                    <img
                      src={img.src}
                      alt={img.alt}
                      loading="eager"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div
                      aria-hidden
                      className="absolute inset-0 pointer-events-none"
                      style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.6) 100%)" }}
                    />
                    <figcaption className="absolute left-2 right-2 bottom-1.5 text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-white/95 leading-tight">
                      {img.caption}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>


            {/* Icon rail — the 15 things a bio page can hold. Each card has
                a small always-visible label so the whole rail reads at a
                glance without needing a hover state on mobile. Floating
                animation is staggered so the grid feels alive. */}
            <div
              className="mt-8 sm:mt-10 grid grid-cols-5 gap-x-3 gap-y-4 sm:gap-x-4 sm:gap-y-5 max-w-md sm:max-w-2xl mx-auto"
              data-testid="hero-icon-rail"
            >
              {HERO_ICONS.map((it, idx) => {
                const Icon = it.icon;
                return (
                  <div
                    key={it.label}
                    className="flex flex-col items-center gap-1.5"
                    data-testid={`hero-icon-${it.label.toLowerCase()}`}
                  >
                    <div
                      className={`aspect-square w-full rounded-2xl border-2 border-ink shadow-brutalSm flex items-center justify-center hover:-translate-y-1 hover:shadow-brutal transition-all animate-hero-float ${it.bg}`}
                      style={{ animationDelay: `${(idx % 5) * 140}ms` }}
                      aria-label={it.label}
                    >
                      <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${it.fg}`} strokeWidth={2.5} />
                    </div>
                    <span
                      className="text-[10px] sm:text-[11px] font-bold text-ink/70 tracking-wide text-center leading-tight"
                      data-testid={`hero-icon-label-${it.label.toLowerCase()}`}
                    >
                      {it.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Primary CTA — gradient button with a subtle sheen */}
            <div
              className="mt-12 flex flex-col items-center gap-4 animate-hero-fade"
              style={{ animationDelay: "220ms" }}
            >
              <button
                data-testid="hero-cta-btn"
                onClick={user ? goApp : signIn}
                className="hero-gradient-btn group relative inline-flex items-center justify-center gap-2 border-2 border-ink text-white font-black text-base sm:text-lg px-8 py-4 shadow-brutal hover:-translate-y-0.5 transition-transform"
              >
                <span className="relative z-10">
                  {user ? "Open my Web Link" : "Create FREE Web Link"}
                </span>
                <ArrowUpRight className="w-5 h-5 relative z-10 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={3} />
              </button>
              <p className="text-[11px] text-ink/50 uppercase tracking-widest font-bold">
                No credit card · takes 60 seconds
              </p>
              {/* v147.99.47 — Native app install nudge, right under
                  the hero CTA so mobile visitors have a one-tap path
                  to download the native app. */}
              <div className="mt-2">
                <p className="text-[10px] font-black uppercase tracking-widest text-ink/50 mb-2 text-center">
                  Or grab the app
                </p>
                <AppStoreBadges size="lg" className="justify-center"
                                data-testid="hero-app-store-badges" />
              </div>
            </div>

            {/* Perfect For personas */}
            <div
              className="mt-8 animate-hero-fade"
              style={{ animationDelay: "280ms" }}
              data-testid="hero-perfect-for"
            >
              <p className="text-[10px] font-black uppercase tracking-widest text-ink/50 mb-3">
                Perfect for
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
                {[
                  { emoji: "🎓", label: "Students" },
                  { emoji: "🎥", label: "Creators" },
                  { emoji: "💼", label: "Professionals" },
                  { emoji: "🏪", label: "Businesses" },
                ].map((p) => (
                  <span
                    key={p.label}
                    data-testid={`hero-persona-${p.label.toLowerCase()}`}
                    className="inline-flex items-center gap-2 border-2 border-ink bg-white px-3 py-1.5 text-sm font-bold shadow-brutalSm"
                  >
                    <span aria-hidden="true">{p.emoji}</span>
                    {p.label}
                  </span>
                ))}
              </div>
            </div>

            {/* v146.18 — Smart QR callout. Small icon + one line to
                introduce Smart QR → Smart Link → multiple actions.
                Simple, subtle, links to a QR sales/print landing story.
                Sits between "Perfect For" and the sign-in hint so it
                catches the eye without stealing the hero CTA. */}
            <div
              className="mt-6 flex items-center justify-center animate-hero-fade"
              style={{ animationDelay: "310ms" }}
              data-testid="hero-smart-qr-callout"
            >
              <div className="inline-flex items-center gap-2 border-2 border-ink bg-white px-3 py-2 shadow-brutalSm">
                <QrCode className="w-4 h-4 flex-shrink-0" strokeWidth={2.5} />
                <p className="text-xs sm:text-sm font-bold text-ink">
                  <span className="font-black">Smart QR</span> opens your Smart Link — one scan, many actions.
                </p>
              </div>
            </div>

            {/* v146.10 — Inline sign-in block removed; auth now lives on
                its own /signin page. Signed-in users get a lightweight
                "already logged in as X" hint that keeps their identity
                visible without pushing a whole auth surface. */}
            {user && (
              <div
                id="signin"
                className="mt-12 max-w-sm mx-auto scroll-mt-24 animate-hero-fade"
                style={{ animationDelay: "340ms" }}
                data-testid="landing-signin-block-signedin"
              >
                <p className="text-xs text-ink/60 text-center">
                  Signed in as <b className="text-ink">{user.email || user.name}</b>. Not you?{" "}
                  <button
                    onClick={logout}
                    data-testid="landing-switch-account-btn"
                    className="underline underline-offset-4 font-bold text-ink hover:text-coral"
                  >
                    Sign in with a different account
                  </button>
                </p>
              </div>
            )}

            {/* Trust line */}
            <div
              className="mt-10 flex flex-col items-center gap-3 animate-hero-fade"
              style={{ animationDelay: "400ms" }}
              data-testid="hero-trust-line"
            >
              <div className="flex -space-x-2">
                {[
                  "https://images.unsplash.com/photo-1592992584424-bb6c75136025?w=80&h=80&fit=crop",
                  "https://images.unsplash.com/photo-1601618752468-4682783b4e6e?w=80&h=80&fit=crop",
                  "https://images.unsplash.com/photo-1652819674544-a284366b1d0d?w=80&h=80&fit=crop",
                ].map((src, i) => (
                  <img key={i} src={src} alt="creator" className="w-8 h-8 rounded-full border-2 border-ink object-cover" />
                ))}
              </div>
              <p className="text-sm font-semibold text-ink/70">
                Join thousands of people building their digital identity with Linkola.
              </p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* v146.19 — Smart QR explainer. Compact 3-step visual: Scan → Smart
          Link → Actions. Sits directly under the hero so curious visitors
          who noticed the Smart QR callout can dig in without scrolling
          past personas. Section is togglable via `sections.smart_qr_explainer`. */}
      {sections.smart_qr_explainer !== false && (
        <section
          id="smart-qr"
          className="border-b-2 border-ink bg-cream"
          data-testid="smart-qr-explainer-section"
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <div className="text-center mb-8 sm:mb-10">
              <p className="overline text-[10px] sm:text-[11px] mb-2 inline-flex items-center gap-1.5">
                <QrCode className="w-3 h-3" strokeWidth={3} /> Smart QR
              </p>
              <h2 className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tighter">
                One scan. Many actions.
              </h2>
              <p className="text-sm sm:text-base text-ink/70 mt-2 max-w-xl mx-auto">
                Print a single QR on cards, packaging, or store displays. Visitors
                land on your Smart Link and pick what to do next.
              </p>
            </div>

            {/* v149.80 — Realistic Smart QR hero photo. Shows a Linkola
                Smart QR stand next to a phone with the Smart Web Link
                opened, placed inside a real cafe counter scene. Mobile
                stays legible at 360px because the image sits in a brutal
                frame with a decorative animated glow but no overlaid
                text. */}
            <div
              className="relative mb-8 sm:mb-10 max-w-3xl mx-auto"
              data-testid="smart-qr-hero-photo"
            >
              <div
                aria-hidden
                className="absolute inset-0 rounded-2xl blur-2xl opacity-70 animate-hero-float pointer-events-none"
                style={{
                  zIndex: 0,
                  background:
                    "radial-gradient(ellipse at 30% 40%, rgba(167,139,250,0.55), transparent 60%), radial-gradient(ellipse at 70% 60%, rgba(16,185,129,0.5), transparent 60%)",
                }}
              />
              <figure className="relative overflow-hidden border-2 border-ink shadow-brutal bg-ink">
                <img
                  src="/landing/smartqr-weblink.webp"
                  alt="Smartphone showing a Linkola Smart Web Link beside a glowing Smart QR stand on a cafe counter"
                  loading="lazy"
                  className="block w-full h-auto object-cover aspect-[16/9] sm:aspect-[21/9]"
                />
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(167,139,250,0.0) 55%, rgba(167,139,250,0.18) 100%)",
                  }}
                />
                <span className="absolute top-2 left-2 sm:top-3 sm:left-3 inline-flex items-center gap-1 border-2 border-ink bg-mint text-ink px-2 py-0.5 text-[9px] sm:text-[10px] font-black uppercase tracking-widest shadow-brutalSm">
                  <QrCode className="w-3 h-3" strokeWidth={3} /> Live scan
                </span>
              </figure>
            </div>


            {/* 3-step grid — stacks on mobile, row on ≥sm */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-3 relative">
              {[
                {
                  n: 1,
                  icon: QrCode,
                  title: "Scan",
                  body: "Anyone taps your QR with their phone camera — no app install, no account.",
                  bg: "bg-white",
                  testid: "smart-qr-step-scan",
                },
                {
                  n: 2,
                  icon: Sparkles,
                  title: "Smart Link",
                  body: "Your Linkola page opens instantly — personal Web Link, product page, or coupon.",
                  bg: "bg-lavender",
                  testid: "smart-qr-step-link",
                },
                {
                  n: 3,
                  icon: MousePointerClick,
                  title: "Actions",
                  body: "Follow, buy, book, message, tip — every tap goes exactly where you want it.",
                  bg: "bg-sun",
                  testid: "smart-qr-step-actions",
                },
              ].map((s) => (
                <div
                  key={s.n}
                  data-testid={s.testid}
                  className={`relative border-2 border-ink ${s.bg} p-4 sm:p-5 shadow-brutalSm`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-6 h-6 border-2 border-ink bg-ink text-cream inline-flex items-center justify-center font-black text-xs">
                      {s.n}
                    </span>
                    <s.icon className="w-5 h-5" strokeWidth={2.5} />
                    <h3 className="font-display font-black text-lg tracking-tighter">{s.title}</h3>
                  </div>
                  <p className="text-sm text-ink/80 leading-snug">{s.body}</p>
                </div>
              ))}
            </div>

            <p className="text-center mt-6 sm:mt-8 text-xs text-ink/50 uppercase tracking-widest font-bold">
              Works on every phone · no app needed
            </p>

            {/* v146.41 — Live pricing + free-trial ribbon so visitors see
                the current offer at a glance. Admin flips it via
                Apply Settings → Free trial toggle. */}
            {qrPricing?.enabled !== false && (
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3"
                   data-testid="smart-qr-pricing-block">
                <div className={`relative border-2 border-ink p-4 sm:p-5 shadow-brutalSm overflow-hidden ${qrPricing?.free_trial_enabled ? "bg-mint" : "bg-white"}`}
                     data-testid="smart-qr-pricing-card"
                     style={{ backgroundImage: qrPricing?.free_trial_enabled
                       ? "radial-gradient(circle at 100% 0%, rgba(255,255,255,0.6), transparent 55%), linear-gradient(135deg, rgba(16,185,129,0.0) 50%, rgba(16,185,129,0.25) 100%)"
                       : "radial-gradient(circle at 0% 0%, rgba(167,139,250,0.18), transparent 55%), linear-gradient(135deg, rgba(255,255,255,0) 50%, rgba(167,139,250,0.14) 100%)",
                     }}>
                  <span aria-hidden className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-2xl opacity-50 pointer-events-none"
                        style={{ background: "radial-gradient(circle, rgba(16,185,129,0.6), transparent 70%)" }} />
                  <p className="overline text-[10px] mb-1 flex items-center gap-1.5">
                    <QrCode className="w-3 h-3" strokeWidth={3} />
                    {qrPricing?.free_trial_enabled ? "Free trial · limited time" : "Simple pricing · pick your plan"}
                  </p>
                  {/* v147.20 — Two-SKU pricing (monthly vs lifetime). The
                      trial banner still wins when enabled — it makes the
                      hero read "₹0 today" instead of dual prices. */}
                  {qrPricing?.free_trial_enabled ? (
                    <p className="font-display font-black text-2xl sm:text-3xl tracking-tighter"
                       data-testid="smart-qr-price-headline">
                      ₹0 today
                      <span className="text-sm font-bold text-ink/60 ml-2">
                        · usually ₹{qrPricing?.lifetime_price_inr ?? qrPricing?.default_sale_price_inr ?? 999}
                      </span>
                    </p>
                  ) : (
                    <div className="mt-1 grid grid-cols-2 gap-2" data-testid="smart-qr-price-headline">
                      <div className="border-2 border-ink bg-cream p-2.5 rounded-lg" data-testid="smart-qr-price-monthly">
                        <p className="overline text-[10px] font-bold">MONTHLY</p>
                        <p className="font-display font-black text-xl sm:text-2xl tracking-tighter">
                          ₹{qrPricing?.monthly_price_inr ?? 49}
                          <span className="text-[10px] font-bold text-ink/60 ml-1">/mo</span>
                        </p>
                        <p className="text-[10px] text-ink/70 mt-0.5">Cancel anytime</p>
                      </div>
                      <div className="border-2 border-ink bg-mint p-2.5 rounded-lg relative" data-testid="smart-qr-price-lifetime">
                        <span className="absolute -top-2 right-2 bg-ink text-cream text-[9px] font-black px-1.5 py-0.5 rounded uppercase tracking-wide">Best value</span>
                        <p className="overline text-[10px] font-bold">LIFETIME</p>
                        <p className="font-display font-black text-xl sm:text-2xl tracking-tighter">
                          ₹{qrPricing?.lifetime_price_inr ?? qrPricing?.default_sale_price_inr ?? 999}
                        </p>
                        <p className="text-[10px] text-ink/70 mt-0.5">Yours forever</p>
                      </div>
                    </div>
                  )}
                  <p className="text-xs sm:text-sm text-ink/80 mt-2.5">
                    {qrPricing?.free_trial_enabled
                      ? (typeof qrPricing?.free_trial_days_remaining === "number"
                          ? (qrPricing.free_trial_days_remaining === 0
                              ? "Last day — grab your Smart QR before the trial ends."
                              : `Free for the next ${qrPricing.free_trial_days_remaining} day${qrPricing.free_trial_days_remaining === 1 ? "" : "s"} · Sign up and mint yours in seconds.`)
                          : "Free right now · Sign up and mint yours in seconds.")
                      : "Auto-linked to your Web Link the moment you claim it. Switch between monthly and lifetime any time."}
                  </p>
                  <a
                    href="#signin"
                    onClick={() => trackSiteEvent("action_hub_cta", { source: "smart_qr_pricing" })}
                    data-testid="smart-qr-pricing-cta"
                    className="mt-3 inline-flex items-center gap-1.5 border-2 border-ink bg-ink text-cream px-4 py-2 font-bold text-xs sm:text-sm shadow-brutalSm hover:-translate-y-0.5 transition-transform"
                  >
                    {qrPricing?.free_trial_enabled ? "Claim my free QR" : "Get my Smart QR"}
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
                  </a>
                  {/* v148.x — Partner CTA in the pricing card. The
                      empty right-side space in the screenshot is
                      where this lands so shopkeepers who don't want
                      to buy can instead RESELL. */}
                  <button
                    type="button"
                    onClick={() => { setCpSource("landing_pricing"); setCpModalOpen(true); }}
                    data-testid="smart-qr-partner-cta"
                    className="mt-3 sm:ml-2 inline-flex items-center gap-1.5 border-2 border-ink bg-sun text-ink px-4 py-2 font-bold text-xs sm:text-sm shadow-brutalSm hover:-translate-y-0.5 transition-transform"
                  >
                    Sell these · Earn recurring
                    <TrendingUp className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                </div>

                {/* v146.41 — Loyalty coins repeat-customer callout. Sits
                    right next to pricing so the value ladder reads:
                    "buy the QR → get repeat customers via loyalty coins". */}
                <div className="relative border-2 border-ink bg-sun p-4 sm:p-5 shadow-brutalSm overflow-hidden"
                     data-testid="smart-qr-loyalty-card"
                     style={{ backgroundImage: "radial-gradient(circle at 100% 100%, rgba(255,255,255,0.55), transparent 55%), linear-gradient(135deg, rgba(255,222,71,0) 50%, rgba(251,146,60,0.3) 100%)" }}>
                  <span aria-hidden className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full blur-2xl opacity-55 pointer-events-none"
                        style={{ background: "radial-gradient(circle, rgba(251,146,60,0.6), transparent 70%)" }} />
                  <p className="overline text-[10px] mb-1 flex items-center gap-1.5">
                    <Coins className="w-3 h-3" strokeWidth={3} /> Repeat customers, built-in
                  </p>
                  <p className="font-display font-black text-lg sm:text-xl tracking-tighter">
                    Loyalty Coins turn every scan into a comeback.
                  </p>
                  <ul className="mt-2 space-y-1.5 text-xs sm:text-sm text-ink/85">
                    <li className="flex items-start gap-1.5">
                      <Repeat className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={2.5} />
                      Reward follows, reviews, referrals &amp; purchases with coins customers can spend at your checkout.
                    </li>
                    <li className="flex items-start gap-1.5">
                      <Coins className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={2.5} />
                      Redeem offline at your counter — one QR scan is all it takes.
                    </li>
                    <li className="flex items-start gap-1.5">
                      <TrendingUp className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={2.5} />
                      Track lifetime coin balances per customer, right from your dashboard.
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* v149.17 — Quick Promote explainer. Sits directly under the Smart QR
          section so the landing story reads "get scanned" then "get seen
          nearby". Deliberately icon-led and short — five points, no
          paragraphs. Togglable via `sections.quick_promote_explainer`
          (absent key = visible, same convention as smart_qr_explainer). */}
      {sections.quick_promote_explainer !== false && (
        <section
          id="quick-promote"
          className="border-b-2 border-ink bg-white"
          data-testid="quick-promote-explainer-section"
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <div className="text-center mb-6 sm:mb-8">
              <p className="overline text-[10px] sm:text-[11px] mb-2 inline-flex items-center gap-1.5">
                <Rocket className="w-3 h-3" strokeWidth={3} /> Quick Promote
              </p>
              <h2 className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tighter">
                Promote in 1000s of nearby shops.
              </h2>
              <p className="text-sm sm:text-base text-ink/70 mt-2">
                Your promo shows on real local places around you.
              </p>

              {/* Where it shows — icon chips, not a sentence. */}
              <div className="flex flex-wrap justify-center gap-2 mt-4"
                   data-testid="quick-promote-places">
                {[
                  { icon: Store, label: "Shops" },
                  { icon: Coffee, label: "Tea shops" },
                  { icon: Utensils, label: "Cafés" },
                  { icon: MapPin, label: "More nearby" },
                ].map((p) => (
                  <span
                    key={p.label}
                    className="inline-flex items-center gap-1.5 border-2 border-ink bg-cream px-2.5 py-1 text-xs font-bold"
                  >
                    <p.icon className="w-3.5 h-3.5" strokeWidth={2.5} /> {p.label}
                  </span>
                ))}
              </div>
            </div>

            {/* 4 short points — stacks on mobile, 2-up on sm, row on lg */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-3">
              <div className="border-2 border-ink bg-lavender p-4 sm:p-5 shadow-brutalSm min-w-0"
                   data-testid="quick-promote-point-pay">
                <div className="flex items-center gap-2 mb-2">
                  <Coins className="w-5 h-5 shrink-0" strokeWidth={2.5} />
                  <h3 className="font-display font-black text-base sm:text-lg lg:text-base tracking-tighter">
                    Pay for real views
                  </h3>
                </div>
                <p className="text-sm text-ink/80 leading-snug">
                  Only when someone actually views or acts.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <span className="inline-flex items-center gap-1 border-2 border-ink bg-white px-2 py-0.5 text-[11px] font-bold">
                    <Eye className="w-3 h-3" strokeWidth={2.5} /> Views
                  </span>
                  <span className="inline-flex items-center gap-1 border-2 border-ink bg-white px-2 py-0.5 text-[11px] font-bold">
                    <MousePointerClick className="w-3 h-3" strokeWidth={2.5} /> Taps
                  </span>
                </div>
              </div>

              <div className="border-2 border-ink bg-mint p-4 sm:p-5 shadow-brutalSm min-w-0"
                   data-testid="quick-promote-point-targeting">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 shrink-0" strokeWidth={2.5} />
                  <h3 className="font-display font-black text-base sm:text-lg lg:text-base tracking-tighter">
                    Location-targeted
                  </h3>
                </div>
                <p className="text-sm text-ink/80 leading-snug">
                  Choose exactly where you promote.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  <span className="inline-flex items-center gap-1 border-2 border-ink bg-white px-2 py-0.5 text-[11px] font-bold">
                    <MapPin className="w-3 h-3" strokeWidth={2.5} /> Your city
                  </span>
                  <span className="inline-flex items-center gap-1 border-2 border-ink bg-white px-2 py-0.5 text-[11px] font-bold">
                    <MapPin className="w-3 h-3" strokeWidth={2.5} /> Your area
                  </span>
                </div>
              </div>

              <div className="border-2 border-ink bg-sun p-4 sm:p-5 shadow-brutalSm min-w-0"
                   data-testid="quick-promote-point-radius">
                <div className="flex items-center gap-2 mb-2">
                  <Radius className="w-5 h-5 shrink-0" strokeWidth={2.5} />
                  <h3 className="font-display font-black text-base sm:text-lg lg:text-base tracking-tighter">
                    Choose your radius
                  </h3>
                </div>
                <p className="text-sm text-ink/80 leading-snug">
                  Promote within 1 km of my location.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {[
                    { r: "500 m", on: false },
                    { r: "1 km", on: true },
                    { r: "5 km", on: false },
                  ].map((o) => (
                    <span
                      key={o.r}
                      className={`inline-flex items-center border-2 border-ink px-2 py-0.5 text-[11px] font-bold ${o.on ? "bg-ink text-cream" : "bg-white text-ink"}`}
                    >
                      {o.r}
                    </span>
                  ))}
                </div>
              </div>

              <div className="border-2 border-ink bg-cream p-4 sm:p-5 shadow-brutalSm min-w-0"
                   data-testid="quick-promote-point-map">
                <div className="flex items-center gap-2 mb-2">
                  <MapPinned className="w-5 h-5 shrink-0" strokeWidth={2.5} />
                  <h3 className="font-display font-black text-base sm:text-lg lg:text-base tracking-tighter">
                    Where views come from
                  </h3>
                </div>
                <p className="text-sm text-ink/80 leading-snug">
                  Exact map spots, not just numbers.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {["2nd Street", "North Corner", "Near ABC Café"].map((spot) => (
                    <span
                      key={spot}
                      className="inline-flex items-center gap-1 border-2 border-ink bg-white px-2 py-0.5 text-[11px] font-bold max-w-full"
                    >
                      <MapPin className="w-3 h-3 shrink-0" strokeWidth={2.5} />
                      <span className="truncate">{spot}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="text-center mt-6 sm:mt-8">
              <a
                href="#signin"
                onClick={() => trackSiteEvent("action_hub_cta", { source: "quick_promote_explainer" })}
                data-testid="quick-promote-cta"
                className="inline-flex items-center gap-1.5 border-2 border-ink bg-ink text-cream px-4 py-2 font-bold text-xs sm:text-sm shadow-brutalSm hover:-translate-y-0.5 transition-transform"
              >
                Start a promotion
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* v146.41 — Admin-managed video showcase. Renders under Smart QR
          so first-time visitors see real Linkola in motion. */}
      {sections.landing_videos !== false && <LandingVideosSection />}

      {/* Live Bio Page preview — v145.5. */}
      {sections.live_preview !== false && <LivePreviewSection />}

      {/* Marquee */}
      {sections.marquee !== false && (
      <div className="border-b-2 border-ink bg-ink text-cream py-3 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap font-display font-black text-2xl tracking-tighter uppercase">
          {Array(2).fill(0).map((_, k) => (
            <div key={k} className="flex gap-12 mr-12">
              {[
                "Smart QR", "Loyalty Coins", "Smart Web Link", "WhatsApp Enquiry",
                "Google Reviews", "Product Sales", "Service Sales",
                "Promotions", "Influencer Affiliates", "Broadcast Offers",
                "Coupons + Leads", "Storefront", "QR Analytics", "One tap Reviews",
              ].map((t, i) => (
                <span key={`${k}-${i}`} className="flex items-center gap-12">
                  <span>{t}</span>
                  <span className="text-coral">★</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Features bento — Digital Insights (in review — greyed out with beta note) */}
      {sections.digital_insights && (
      <section id="features" className="border-b-2 border-ink relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="max-w-2xl mb-6">
            <div className="inline-flex items-center gap-2 border-2 border-ink bg-sun px-3 py-1 mb-4 shadow-brutalSm">
              <span className="text-[9px] font-black uppercase tracking-widest">Coming soon · free while in beta</span>
            </div>
            <p className="overline mb-3">Digital Insights · ten tools · in Meta review</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">
              Tools that look as good as your feed.
            </h2>
            <p className="mt-4 text-ink/70 max-w-xl">
              Full analytics + AI coaching once we&apos;re Meta-approved. <b>Free while in beta</b> for anyone signed up now — you&apos;ll get an email the day we go live.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 opacity-70" data-testid="features-bento">
            {/* AI Coach — hero feature */}
            <Feature
              span="md:col-span-7"
              bg="bg-sun"
              tag="NEW"
              icon={<MessageSquare className="w-6 h-6" strokeWidth={3} />}
              title="AI Growth Coach that knows YOUR numbers"
              desc="Chat with a senior strategist trained on your last 60 posts. Ask 'what should I post tomorrow?' and get answers grounded in your real top hashtags, best slot, and avg engagement. Suggested follow-ups included."
            />
            <Feature
              span="md:col-span-5"
              bg="bg-mint"
              tag="NEW"
              icon={<Swords className="w-6 h-6" strokeWidth={3} />}
              title="A/B Test before you post"
              desc="Two captions, two hashtag sets, two images, two posters — or two reels. AI picks the winner with confidence scores + reasoning."
            />

            {/* Reels analyzer */}
            <Feature
              span="md:col-span-5"
              bg="bg-bubble"
              tag="NEW"
              icon={<Film className="w-6 h-6" strokeWidth={3} />}
              title="Reel Analyzer"
              desc="Upload a draft reel. Gemini watches it and scores hook (first 3s), pacing, retention risk, text legibility + suggests a better first frame."
            />
            {/* Velocity */}
            <Feature
              span="md:col-span-7"
              bg="bg-lavender"
              tag="NEW"
              icon={<Gauge className="w-6 h-6" strokeWidth={3} />}
              title="Engagement Velocity tracker"
              desc="See which posts spiked in the first hour (algorithm-loved fast-burners) vs the ones that trickle in saves over days. Double down on what burns fast."
            />

            {/* Analytics dashboard */}
            <Feature
              span="md:col-span-7"
              bg="bg-cream"
              icon={<LineChart className="w-6 h-6" strokeWidth={3} />}
              title="Bento dashboard with audience demographics"
              desc="Best slots, hottest content type, engagement trend, top posts, gender × age split, top countries + cities, AND follower-vs-non-follower reach — one screen."
            />
            <Feature
              span="md:col-span-5"
              bg="bg-page"
              icon={<UserCheck className="w-6 h-6" strokeWidth={3} />}
              title="Follower vs Non-Follower reach"
              desc="Aggregated across your Reels — see exactly how much of your reach is converting strangers into followers."
            />

            {/* Hashtag performance + optimizer + analyzer */}
            <Feature
              span="md:col-span-4"
              bg="bg-sun"
              icon={<TrendingUp className="w-6 h-6" strokeWidth={3} />}
              title="Hashtag Performance"
              desc="See which hashtags actually drove engagement on YOUR past posts — not generic suggestions."
            />
            <Feature
              span="md:col-span-4"
              bg="bg-bubble"
              icon={<Hash className="w-6 h-6" strokeWidth={3} />}
              title="Hashtag Optimizer"
              desc="Three tiers: high-reach, medium, niche. Stops you from blending into the #love void."
            />
            <Feature
              span="md:col-span-4"
              bg="bg-mint"
              icon={<Sparkles className="w-6 h-6" strokeWidth={3} />}
              title="Draft Analyzer"
              desc="Score captions on hook, CTA, readability, and engagement potential. AI rewrites in one click."
            />

            {/* Weekly + Workspace */}
            <Feature
              span="md:col-span-7"
              bg="bg-lavender"
              icon={<CalendarDays className="w-6 h-6" strokeWidth={3} />}
              title="Weekly recommendation engine"
              desc="An AI-generated plan for the next 7 days: themes, hooks, exact posting times. Show up consistent."
            />
            <Feature
              span="md:col-span-5"
              bg="bg-coral text-white"
              icon={<Users className="w-6 h-6 text-white" strokeWidth={3} />}
              title="Multi-account workspace"
              desc="Manage multiple creator accounts side-by-side (Instagram + Facebook in Meta review; YouTube, LinkedIn, TikTok rolling out soon). Built for D2C brands and agencies."
              dark
            />
          </div>
        </div>
      </section>
      )}

      {/* Reels-focused section */}
      {sections.digital_insights && (
      <section id="reels" className="border-b-2 border-ink bg-ink text-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="inline-flex items-center gap-2 border-2 border-cream bg-coral text-white px-3 py-1 mb-6">
              <Film className="w-4 h-4" strokeWidth={3} />
              <span className="overline">Built for Reels</span>
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95]">
              Reels are 70% of growth.<br />
              <span className="bg-sun text-ink border-2 border-cream px-2 inline-block mt-2">We treat them that way.</span>
            </h2>
            <p className="mt-6 text-cream/80 max-w-xl">
              Linkola is the only AI coach that watches your draft <i>before</i> you post.
              Upload your reel — Gemini scores hook, pacing, retention risk, and on-screen text. A/B two reels head-to-head and let AI pick the scroll-stopper.
            </p>
            <ul className="mt-6 space-y-2">
              {[
                "First-3-second hook scoring",
                "Pacing + retention risk analysis",
                "On-screen text legibility audit",
                "Side-by-side A/B with confidence",
                "Suggested first-frame direction",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm font-semibold">
                  <CheckCircle2 className="w-4 h-4 text-mint" strokeWidth={3} />
                  {t}
                </li>
              ))}
            </ul>
            <button data-testid="reels-cta-btn" onClick={user ? goApp : signIn} className="mt-8 inline-flex items-center gap-2 bg-coral text-white border-2 border-cream px-4 py-3 font-bold uppercase text-sm tracking-wider shadow-brutalSm">
              Try the Reel Analyzer <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>

          <div className="md:col-span-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-page text-ink border-2 border-cream p-5 -rotate-1">
                <p className="overline">Variant A · Reel</p>
                <p className="font-display font-black text-5xl tracking-tighter mt-2">71<span className="text-base text-ink/60">/100</span></p>
                <ul className="mt-3 text-xs space-y-1 font-bold">
                  <li className="flex justify-between"><span>Hook</span><span>78</span></li>
                  <li className="flex justify-between"><span>Pacing</span><span>68</span></li>
                  <li className="flex justify-between"><span>Retention</span><span>72</span></li>
                </ul>
              </div>
              <div className="bg-mint text-ink border-2 border-cream p-5 rotate-1 shadow-brutal">
                <div className="flex items-center justify-between">
                  <p className="overline">Variant B · WINNER ★</p>
                  <Swords className="w-4 h-4" strokeWidth={3} />
                </div>
                <p className="font-display font-black text-5xl tracking-tighter mt-2">86<span className="text-base text-ink/60">/100</span></p>
                <ul className="mt-3 text-xs space-y-1 font-bold">
                  <li className="flex justify-between"><span>Hook</span><span>91</span></li>
                  <li className="flex justify-between"><span>Pacing</span><span>84</span></li>
                  <li className="flex justify-between"><span>Retention</span><span>83</span></li>
                </ul>
              </div>
              <div className="col-span-2 bg-sun text-ink border-2 border-cream p-5">
                <p className="overline">AI verdict · high confidence</p>
                <p className="text-sm font-semibold mt-2">
                  B wins on first-3-second hook — opens on a tight close-up + question overlay. A is too slow to reveal what the video is about. Recommendation: re-cut A&apos;s first 1.5s to match B&apos;s pattern.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Action Hub / CTA Hub section */}
      {sections.action_hub_features && (
      <section id="action-hub" className="border-b-2 border-ink bg-page">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="grid md:grid-cols-12 gap-10 items-start mb-14">
            <div className="md:col-span-6">
              <div className="inline-flex items-center gap-2 border-2 border-ink bg-coral text-white px-3 py-1 mb-6 shadow-brutalSm">
                <Rocket className="w-4 h-4" strokeWidth={3} />
                <span className="overline">Live today · No social account required</span>
              </div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[0.95]">
                Your Linktree is a dead end.<br />
                <span className="bg-sun border-2 border-ink shadow-brutalSm px-2 inline-block mt-2">
                  Build a CTA Hub that <i>converts</i>.
                </span>
              </h2>
              <p className="mt-6 text-lg text-ink/80 max-w-xl leading-relaxed">
                Ship high-converting landing pages in minutes — <b>no code</b>. Buttons, products,
                lead forms, coupons, brand collabs. Track every click.
              </p>
              <ul className="mt-6 space-y-2 text-sm font-semibold">
                {[
                  "24 no-code block types (WhatsApp, Products, Coupons, Lead forms, Location…)",
                  "11 pro themes + custom Google Fonts",
                  "Brand Campaign Marketplace — earn commission on every sale",
                  "Full click → conversion → revenue attribution",
                  "Public Creator Hub at /u/your-handle",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-leaf flex-shrink-0" strokeWidth={3} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <button data-testid="action-hub-cta-btn" onClick={user ? () => navigate("/app/action-hub") : signIn} className="btn-primary">
                  {user ? "Open Action Hub" : "Try Action Hub free"} <ArrowUpRight className="w-4 h-4" />
                </button>
                <Link to="/marketplace" className="btn-secondary" data-testid="action-hub-marketplace-btn">Browse Marketplace</Link>
              </div>
            </div>

            {/* Right: mock CTA Hub preview */}
            <div className="md:col-span-6">
              <div className="brutal-card p-4 bg-cream max-w-md ml-auto">
                <div className="border-2 border-ink bg-white p-4">
                  <div className="text-center mb-4">
                    <div className="w-16 h-16 rounded-full border-2 border-ink bg-coral mx-auto mb-2 flex items-center justify-center text-white font-display font-black text-2xl">B</div>
                    <p className="font-display font-black text-lg tracking-tighter">@bharath</p>
                    <p className="text-xs text-ink/60">creator · 42k followers</p>
                  </div>
                  <div className="space-y-2">
                    <MockBlock icon={<MessageSquare className="w-3 h-3" strokeWidth={3} />} label="WhatsApp me" tone="bg-mint" clicks="1.2k" />
                    <MockBlock icon={<Rocket className="w-3 h-3" strokeWidth={3} />} label="Download our app" tone="bg-sun" clicks="890" />
                    <MockBlock icon={<Tag className="w-3 h-3" strokeWidth={3} />} label="Coupon: SAVE20 · 20% off" tone="bg-coral text-white" clicks="2.1k" />
                    <MockBlock icon={<ShoppingBag className="w-3 h-3" strokeWidth={3} />} label="Shop the merch · ₹1,299" tone="bg-bubble" clicks="420" />
                    <MockBlock icon={<Globe className="w-3 h-3" strokeWidth={3} />} label="Book a discovery call" tone="bg-lavender" clicks="132" />
                  </div>
                  <p className="text-[10px] text-ink/50 text-center mt-3">
                    Every click tracked · CTA breakdown in analytics
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature grid — Action Hub */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="action-hub-features">
            <Feature
              span="md:col-span-4"
              bg="bg-coral text-white"
              icon={<MousePointerClick className="w-6 h-6 text-white" strokeWidth={3} />}
              title="Per-CTA click analytics"
              desc="See exactly which button — WhatsApp, App Download, coupon, product — is pulling clicks. Break down by block type across every page."
              dark
            />
            <Feature
              span="md:col-span-4"
              bg="bg-mint"
              icon={<Share2 className="w-6 h-6" strokeWidth={3} />}
              title="Shareable analytics links"
              desc="Send a brand partner a public or email-gated URL to your page&apos;s visitor stats. Perfect for wrap-up decks + sponsor reports."
            />
            <Feature
              span="md:col-span-4"
              bg="bg-sun"
              icon={<Palette className="w-6 h-6" strokeWidth={3} />}
              title="11 themes · 6 templates"
              desc="Y2K, Neon, Editorial, Handwritten, Print, Neubrutalist and more. Start from Link-in-Bio, Product Launch, App Launch, Freelancer templates."
            />
            <Feature
              span="md:col-span-5"
              bg="bg-lavender"
              icon={<Tag className="w-6 h-6" strokeWidth={3} />}
              title="Coupons + Products + Leads"
              desc="Ship codes with expiry + usage caps. Sell products with Buy CTAs. Capture leads with configurable forms + CSV exports."
            />
            <Feature
              span="md:col-span-7"
              bg="bg-bubble"
              icon={<LineChart className="w-6 h-6" strokeWidth={3} />}
              title="Full visitor analytics dashboard"
              desc="Daily traffic, funnels, top clicks, referrers, devices, browsers, countries, 7×24 heatmaps. Available at both hub and per-page level."
            />
          </div>
        </div>
      </section>
      )}

      {/* Trending creator pages — social proof + design showcase */}
      {sections.trending_pages && trending.length > 0 && (
        <section id="trending" className="border-b-2 border-ink bg-cream">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
              <div>
                <div className="inline-flex items-center gap-2 border-2 border-ink bg-coral text-white px-3 py-1 mb-4 shadow-brutalSm">
                  <TrendingUp className="w-4 h-4" strokeWidth={3} />
                  <span className="overline">Trending right now</span>
                </div>
                <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter">Pages people are loving</h2>
                <p className="mt-3 text-ink/70 max-w-xl">
                  Real creator hubs, built in Linkola. Click any card to see the design in action — steal ideas for yours.
                </p>
              </div>
              <Link to="/trending" className="btn-secondary" data-testid="trending-see-all" onClick={() => trackSiteEvent("trending_view", { source: "landing" })}>
                See all trending <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {trending.slice(0, 8).map((p) => (
                <TrendingCard key={p.slug} page={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* How it works — CTA Action Hub story (Build → Share → Earn) */}
      {sections.how_it_works_action_hub && (
      <section id="how-action-hub" className="border-b-2 border-ink bg-page" data-testid="how-it-works-action-hub-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <div className="inline-flex items-center gap-2 border-2 border-ink bg-sun px-3 py-1 mb-4 shadow-brutalSm">
            <Rocket className="w-4 h-4" strokeWidth={3} />
            <span className="overline">CTA Action Hub · How it works</span>
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-3">Build, share, earn. In that order.</h2>
          <p className="text-ink/70 max-w-2xl mb-12">
            Turn a single link in your bio into a full commerce &amp; content engine. No code. Live in under 5 minutes.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                n: "01",
                icon: Palette,
                tone: "bg-mint",
                t: "Build your Web Link & Pages",
                d: "Design a public /u/{you} hub in the drag-and-drop editor. Add Action Pages for launches, waitlists, lead magnets, product drops — anything you'd normally build a Linktree or landing page for. Every block is trackable.",
              },
              {
                n: "02",
                icon: Share2,
                tone: "bg-sun",
                t: "Drop the link in every bio",
                d: "Paste your Linkola link in Instagram, YouTube, TikTok, X and every DM. Real followers land on a page that actually converts — we measure every tap, scroll and click so you know what's working (and grow the audience that already trusts you).",
              },
              {
                n: "03",
                icon: ShoppingBag,
                tone: "bg-coral text-white",
                t: "Earn from what you post",
                d: "Sell products, run paid coupons, accept brand campaigns from the Marketplace, run affiliate promotions, or promote your own drops — payouts land in your Linkola Wallet with instant withdrawal to UPI or bank.",
              },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.n} className="brutal-card p-8 flex flex-col" data-testid={`how-it-works-action-hub-step-${s.n}`}>
                  <div className={`inline-flex items-center gap-2 border-2 border-ink ${s.tone} px-3 py-1.5 self-start shadow-brutalSm mb-4`}>
                    <Icon className="w-4 h-4" strokeWidth={3} />
                    <span className="overline">Step {s.n}</span>
                  </div>
                  <h3 className="font-display font-black text-2xl mb-2 tracking-tighter">{s.t}</h3>
                  <p className="text-ink/80 text-sm leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* How it works — Digital Insights story (Connect → Analyse → Execute) */}
      {sections.how_it_works_di && (
      <section id="how" className="border-b-2 border-ink bg-cream" data-testid="how-it-works-di-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <p className="overline mb-3">Digital Insights · How it works</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-12">Three steps. Ten tools.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Connect", d: "Sign in with Google. Link Instagram or Facebook Page today; YouTube, LinkedIn and TikTok connectors are rolling out this quarter. Try demo mode if you want to look first." },
              { n: "02", t: "Analyse", d: "We crunch your last 60 posts in seconds — engagement, demographics, hashtag winners, velocity curves." },
              { n: "03", t: "Execute", d: "Chat with the AI Coach, A/B test before publishing, follow the weekly plan. Post smarter immediately." },
            ].map((s) => (
              <div key={s.n} className="brutal-card p-8">
                <p className="font-display font-black text-6xl tracking-tighter text-coral">{s.n}</p>
                <h3 className="font-display font-black text-2xl mt-3 mb-2">{s.t}</h3>
                <p className="text-ink/80">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Bio-hub examples — different themes + block combinations */}
      {sections.bio_examples && <BioPageExamplesSection showPricing={sections.pricing !== false} />}

      {/* v146.77 — "Built for your business" strip. 10 tiles → one per
          vertical, each linking to /for/{vertical} so visitors can
          discover the SEO landing pages without knowing the URL. */}
      {sections.vertical_strip !== false && <VerticalStripSection />}

      {/* Influencer workflow — Discover → Apply → Promote → Earn */}
      {sections.influencer_flow && <InfluencerFlowSection />}

      {/* Brand workflow — Post → Discover → Invite → Track → Pay */}
      {sections.brand_flow && <BrandFlowSection />}

      {/* Pricing */}
      {sections.pricing && (
      <section id="pricing" className="border-b-2 border-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
          <p className="overline mb-3">Pricing · CTA Action Hub — live today</p>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mb-4">Start free. Scale loud.</h2>
          <p className="text-sm text-ink/70 mb-6 max-w-2xl">
            These are our <b>CTA Action Hub</b> plans — live and usable today, no social account needed.
            {sections.digital_insights && (
              <> Digital Insights (Instagram + Facebook analytics) unlocks automatically once Meta approves us,
              and is <b>free while in beta</b> for anyone who signs up now.</>
            )}
          </p>
          {/* v147.20 — Monthly / Yearly toggle. Sits above the grid so
              users pick their preferred cadence before scanning cards.
              We render only the plans matching the selected cycle
              (plus the free "launch" tier, which has interval_days=0
              and shows on both). */}
          <div className="mb-6 inline-flex items-center gap-1 border-2 border-ink bg-white p-1 shadow-brutalSm rounded-lg" data-testid="pricing-cycle-toggle">
            {[
              { key: "monthly", label: "Monthly" },
              { key: "yearly", label: "Yearly", badge: "Save more" },
            ].map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setPricingCycle(c.key)}
                className={`px-4 py-1.5 rounded-md text-xs sm:text-sm font-bold flex items-center gap-1.5 transition-colors ${pricingCycle === c.key ? "bg-ink text-cream" : "text-ink/70 hover:bg-cream"}`}
                data-testid={`pricing-cycle-${c.key}`}
              >
                {c.label}
                {c.badge && pricingCycle !== c.key && (
                  <span className="text-[9px] bg-mint text-ink font-black px-1.5 py-0.5 rounded uppercase">{c.badge}</span>
                )}
              </button>
            ))}
          </div>
          <div className="grid md:grid-cols-3 gap-6" data-testid="pricing-grid">
            {plans.filter((p) => (p.family || "insights") === "action_hub").length === 0 ? (
              // Fallback while plans load (or if admin hasn't published any Action Hub plans yet)
              <p className="col-span-full text-sm text-ink/60" data-testid="pricing-empty">
                Loading plans…
              </p>
            ) : (
              (() => {
                // v147.20 — Group by tier, pick the variant matching the
                // selected cycle. Free plans (price_inr === 0 / interval_days === 0)
                // show in both cycles so the "Personal" tile is always visible.
                const activeAhPlans = plans.filter((p) => (p.family || "insights") === "action_hub" && p.active !== false);
                const byTier = new Map();
                for (const p of activeAhPlans) {
                  const tier = p.tier || p.plan_id.replace(/[-_](monthly|yearly)$/, "");
                  if (!byTier.has(tier)) byTier.set(tier, []);
                  byTier.get(tier).push(p);
                }
                const tiers = [...byTier.entries()]
                  .sort((a, b) => Math.min(...a[1].map((p) => p.price_inr)) - Math.min(...b[1].map((p) => p.price_inr)));
                return tiers.map(([tier, variants]) => {
                  const chosen = variants.find((v) => (v.billing_cycle || (v.interval_days === 30 ? "monthly" : v.interval_days === 365 ? "yearly" : "custom")) === pricingCycle)
                    || variants.find((v) => v.price_inr === 0)
                    || variants[0];
                  return (
                    <PricingCard
                      key={chosen.plan_id}
                      tier={chosen.name}
                      priceInr={chosen.price_inr}
                      mrpInr={chosen.mrp_inr}
                      offerEndsAt={chosen.offer_ends_at}
                      offerLabel={chosen.offer_label}
                      features={chosen.features || []}
                      highlighted={chosen.highlighted}
                      intervalDays={chosen.interval_days}
                      billingCycle={chosen.billing_cycle}
                      onCta={signIn}
                    />
                  );
                });
              })()
            )}
          </div>
          {sections.digital_insights && (
            <div className="mt-10 border-2 border-dashed border-ink/40 bg-cream p-4 sm:p-6" data-testid="digital-insights-notice">
              <div className="flex items-start gap-3 flex-wrap">
                <div className="border-2 border-ink bg-sun px-2 py-0.5 text-[10px] font-black uppercase tracking-widest">Coming soon</div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold">Digital Insights (Instagram + Facebook analytics)</p>
                  <p className="text-sm text-ink/70 mt-1">
                    AI Coach, Reel A/B, Engagement Velocity, Audience demographics, Hashtag Optimizer,
                    Weekly plans, Best-time-to-post, Boost Recommendations, Ads ROI. Meta review in progress.
                    <b> Free while in beta</b> — sign up now, connect the moment we&apos;re approved.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      )}

      {/* CTA */}
      {sections.final_cta && (
      <section className="border-b-2 border-ink bg-coral text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 text-center">
          <h2 className="font-display font-black text-5xl sm:text-7xl tracking-tighter leading-none mb-6">
            Go live in 5 minutes.<br /> No social account needed.
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-8">
            Free to start. Build your first Action Page, activate a brand campaign, get paid.
            {sections.digital_insights && (<> Digital Insights unlocks free the moment Meta approves us.</>)}
          </p>
          <button data-testid="footer-cta-btn" onClick={user ? goApp : signIn} className="btn-secondary">
            {user ? "Open CTA Action Hub" : "Sign up in 5 minutes"} <ArrowUpRight className="w-4 h-4" />
          </button>
          {!user && (
            <div className="mt-6 pt-6 border-t-2 border-white/30">
              <p className="text-white/80 text-sm mb-3">
                Or — <b>earn recurring income by selling Linkola loyalty QRs to nearby shops.</b>
              </p>
              <button
                type="button"
                onClick={() => { setCpSource("landing_footer"); setCpModalOpen(true); }}
                data-testid="footer-partner-cta"
                className="inline-flex items-center gap-2 border-2 border-white bg-sun text-ink px-5 py-2.5 text-sm font-black shadow-brutalSm"
              >
                <TrendingUp className="w-4 h-4" strokeWidth={2.5} /> Become a Channel Partner
              </button>
            </div>
          )}
        </div>
      </section>
      )}

      <footer className="bg-page" data-landing-part="footer">
        <div className="max-w-7xl mx-auto px-6 py-8 space-y-4">
          {/* v147.99.47 — App store badges in the footer too. Lets
              users who scrolled all the way to the bottom grab the
              native app without hunting for the hero CTA. */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-[10px] font-black uppercase tracking-widest text-ink/60">
              Take Linkola with you
            </p>
            <AppStoreBadges size="sm" data-testid="footer-app-store-badges" />
          </div>
          <div className="border-t-2 border-ink/10 pt-4 flex flex-wrap justify-between items-center gap-4 text-sm">
            <p className="font-bold">© Linkola · Bizsaclabs Softtech Pvt Ltd</p>
            <nav className="flex flex-wrap items-center gap-4 text-xs font-bold">
              <a href="/terms" data-testid="footer-terms-link" className="underline-offset-4 hover:underline">Terms</a>
              <a href="/privacy" data-testid="footer-privacy-link" className="underline-offset-4 hover:underline">Privacy</a>
              <Link to="/for/personal" data-testid="footer-personal-link" className="underline-offset-4 hover:underline">Personal pages</Link>
              <Link to="/for/creators" data-testid="footer-creators-link" className="underline-offset-4 hover:underline">For creators</Link>
              <span className="text-ink/60 inline-flex items-center gap-1.5">
                <Globe className="w-3 h-3" /> Built for creators &amp; D2C brands worldwide
              </span>
            </nav>
          </div>
        </div>
      </footer>

      {/* v146.41 — Floating WhatsApp support button. Silent when the
          admin hasn't configured a number. Fires a `landing_whatsapp_click`
          site event on tap for super-admin analytics. */}
      <FloatingWhatsAppButton />

      {/* v148.x — Channel Partner application modal (shared across
          header / pricing / footer entry points). */}
      <ChannelPartnerModal
        open={cpModalOpen}
        onClose={() => setCpModalOpen(false)}
        referralSource={cpSource}
      />
      <ContactSalesModal open={salesOpen} onClose={() => setSalesOpen(false)} source="landing" />
    </div>
  );
}

const Feature = ({ span, bg, icon, title, desc, tag, dark }) => (
  <div className={`${span} ${bg} border-2 border-ink shadow-brutal p-7 relative`}>
    {tag && (
      <span className={`absolute -top-3 -right-3 text-[10px] font-black uppercase tracking-widest border-2 border-ink px-2 py-1 ${dark ? "bg-sun text-ink" : "bg-coral text-white"}`}>
        {tag}
      </span>
    )}
    <div className={`w-12 h-12 ${dark ? "bg-ink" : "bg-white"} border-2 border-ink shadow-brutalSm flex items-center justify-center mb-4`}>{icon}</div>
    <h3 className="font-display font-black text-2xl tracking-tight mb-2">{title}</h3>
    <p className={`${dark ? "text-white/80" : "text-ink/80"} leading-relaxed`}>{desc}</p>
  </div>
);

const MockBlock = ({ icon, label, tone, clicks }) => (
  <div className={`border-2 border-ink px-3 py-2 shadow-brutalSm flex items-center justify-between gap-2 ${tone}`}>
    <div className="flex items-center gap-1.5 min-w-0">
      <div className="border border-ink bg-white text-ink p-0.5 flex-shrink-0">{icon}</div>
      <span className="text-xs font-bold truncate">{label}</span>
    </div>
    <span className="text-[10px] font-mono opacity-70 flex-shrink-0">{clicks}</span>
  </div>
);

// Inline brand SVGs — no external deps. Kept monochrome so the pill's `tone`
// controls color; brand recognisability comes from the shape.
const PLATFORM_ICON = {
  instagram: (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden>
      <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9c-.42-.42-.68-.82-.9-1.38-.16-.42-.36-1.06-.41-2.23C2.17 15.58 2.16 15.2 2.16 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zm0 5.68a4.16 4.16 0 1 0 0 8.32 4.16 4.16 0 0 0 0-8.32zm5.32-1.36a.98.98 0 1 0 0 1.96.98.98 0 0 0 0-1.96zM12 10a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" />
    </svg>
  ),
  youtube: (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden>
      <path d="M23.5 6.2c-.3-1-1.1-1.8-2.1-2C19.6 4 12 4 12 4s-7.6 0-9.4.2c-1 .2-1.8 1-2.1 2C.3 8 .3 12 .3 12s0 4 .2 5.8c.3 1 1.1 1.8 2.1 2C4.4 20 12 20 12 20s7.6 0 9.4-.2c1-.2 1.8-1 2.1-2 .2-1.8.2-5.8.2-5.8s0-4-.2-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z" />
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden>
      <path d="M20.4 3H3.6C3.3 3 3 3.3 3 3.6v16.8c0 .3.3.6.6.6h16.8c.3 0 .6-.3.6-.6V3.6c0-.3-.3-.6-.6-.6zM8.3 18.3H5.6V9.6h2.7v8.7zM6.9 8.4a1.6 1.6 0 1 1 0-3.2 1.6 1.6 0 0 1 0 3.2zm11.4 9.9h-2.7v-4.2c0-1 0-2.3-1.4-2.3s-1.6 1.1-1.6 2.2v4.3H10V9.6h2.6v1.2h.1c.4-.7 1.3-1.4 2.6-1.4 2.8 0 3.3 1.8 3.3 4.2v4.7z" />
    </svg>
  ),
  tiktok: (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden>
      <path d="M19.6 6.9a4.8 4.8 0 0 1-3.6-1.6 4.8 4.8 0 0 1-1.1-2.3h-3.3V15.2a2.8 2.8 0 1 1-2-2.7v-3.4a6.1 6.1 0 1 0 5.3 6.1V9.4a8 8 0 0 0 4.7 1.5V7.6c-.1-.7 0-.7 0-.7z" />
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="currentColor" aria-hidden>
      <path d="M22 12a10 10 0 1 0-11.6 9.9V15h-2.5v-3h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5H15.2c-1.2 0-1.6.7-1.6 1.5V12H16.4l-.4 3H13.6v6.9A10 10 0 0 0 22 12z" />
    </svg>
  ),
};

const PLATFORM_STATUS_TONE = {
  live: "bg-mint",
  beta: "bg-sun",
  soon: "bg-cream",
  review: "bg-cream",
};

const PLATFORM_STATUS_LABEL = {
  live: "Live",
  beta: "Beta",
  soon: "Soon",
  review: "In review",
};

const PlatformPill = ({ icon, label, status = "live" }) => (
  <span
    className={`inline-flex items-center gap-1.5 border-2 border-ink px-2 py-1 shadow-brutalSm ${PLATFORM_STATUS_TONE[status]}`}
    data-testid={`platform-pill-${icon}`}
    title={`${label} · ${PLATFORM_STATUS_LABEL[status]}`}
  >
    {PLATFORM_ICON[icon]}
    <span className="text-xs font-bold">{label}</span>
    <span className={`text-[8px] font-black uppercase tracking-widest border border-ink px-1 py-0 leading-none ${
      status === "live" ? "bg-ink text-white" :
      status === "beta" ? "bg-coral text-white" :
      status === "review" ? "bg-sun" : "bg-white"
    }`}>
      {PLATFORM_STATUS_LABEL[status]}
    </span>
  </span>
);

const PlatformTag = ({ icon, title, line }) => (
  <div className="border-2 border-ink bg-white p-2.5 shadow-brutalSm" data-testid={`platform-tag-${icon}`}>
    <div className="flex items-center gap-1.5 mb-1">
      <div className="border-2 border-ink bg-cream p-0.5 flex-shrink-0 leading-none">
        {PLATFORM_ICON[icon]}
      </div>
      <p className="text-xs font-black uppercase tracking-wider">{title}</p>
    </div>
    <p className="text-[11px] text-ink/70 leading-snug">{line}</p>
  </div>
);

const PricingCard = ({ tier, priceInr, mrpInr, offerEndsAt, offerLabel,
                       features, highlighted, intervalDays = 30, billingCycle, onCta }) => {
  // v146.86 — Prefer explicit billing_cycle from Admin → Plans; fall
  // back to interval_days heuristic. Never hardcode /mo or /yr.
  const cycle = String(billingCycle || "").toLowerCase();
  const intervalLabel = priceInr === 0
    ? ""
    : cycle === "yearly" || cycle === "annual" ? "/yr"
    : cycle === "monthly" ? "/mo"
    : intervalDays >= 300 ? "/yr"
    : intervalDays >= 28 && intervalDays <= 31 ? "/mo"
    : `/${intervalDays}d`;
  // Format ₹0 as "Free" and everything else with Indian grouping
  const displayPrice = (priceInr === 0 || priceInr == null)
    ? "Free"
    : `₹${Number(priceInr).toLocaleString("en-IN")}`;
  const hasMrp = Number(mrpInr) > 0 && Number(mrpInr) > Number(priceInr);
  const savings = hasMrp ? Math.round(((mrpInr - priceInr) / mrpInr) * 100) : 0;
  const offerActive = (() => {
    if (!offerEndsAt) return hasMrp; // if no deadline, still show as an offer if MRP is set
    const end = new Date(offerEndsAt);
    return !isNaN(end.getTime()) && end.getTime() > Date.now();
  })();

  return (
    <div className={`p-8 border-2 border-ink shadow-brutal ${highlighted ? "bg-sun" : "bg-white"} relative`}
         data-testid={`pricing-card-${(tier || "").toLowerCase().replace(/\s+/g, "-")}`}>
      {highlighted && (
        <span className="absolute -top-3 -right-3 text-[10px] font-black uppercase tracking-widest border-2 border-ink px-2 py-1 bg-coral text-white">
          Most popular
        </span>
      )}
      {offerActive && (
        <span className="absolute -top-3 left-4 text-[10px] font-black uppercase tracking-widest border-2 border-ink px-2 py-1 bg-mint">
          {offerLabel || (savings > 0 ? `Save ${savings}%` : "Launch offer")}
        </span>
      )}
      <p className="overline">{tier}</p>
      <div className="mt-2 mb-1 flex items-baseline gap-2 flex-wrap">
        {hasMrp && (
          <span className="text-lg font-bold text-ink/40 line-through" data-testid="pricing-mrp">
            ₹{Number(mrpInr).toLocaleString("en-IN")}
          </span>
        )}
        <span className="font-display font-black text-5xl tracking-tighter">{displayPrice}</span>
        {intervalLabel && <span className="text-base font-bold text-ink/60">{intervalLabel}</span>}
      </div>
      {offerActive && offerEndsAt && (
        <p className="mb-3 text-xs font-bold text-coral flex items-center gap-1" data-testid="pricing-countdown">
          <Clock className="w-3 h-3" />
          <CountdownLabel iso={offerEndsAt} />
        </p>
      )}
      {!offerActive && <div className="mb-3" />}
      <ul className="space-y-2 mb-6">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-sm font-semibold">
            <span className="w-2 h-2 bg-ink mt-1.5 flex-shrink-0" />
            {f}
          </li>
        ))}
      </ul>
      <button
        onClick={onCta}
        data-testid={`pricing-cta-${(tier || "").toLowerCase().replace(/\s+/g, "-")}`}
        className={highlighted ? "btn-primary w-full justify-center" : "btn-secondary w-full justify-center"}
      >
        {priceInr === 0 ? "Start free" : `Choose ${tier}`}
      </button>
    </div>
  );
};

// Live-ish countdown that re-renders every 60s so users see time draining.
function CountdownLabel({ iso }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  const end = new Date(iso).getTime();
  if (isNaN(end) || end <= now) return <span>Offer ended</span>;
  const secs = Math.floor((end - now) / 1000);
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return <span>Offer ends in {d}d {h}h · {new Date(iso).toLocaleDateString()}</span>;
  if (h > 0) return <span>Offer ends in {h}h {m}m — grab it now</span>;
  return <span>Offer ends in {m}m — hurry!</span>;
}


// ---------------------------------------------------------------------------
// v146.41 — LandingVideosSection
// Renders the admin-configured video grid on the marketing page. Reads
// /api/landing/videos (public) and self-hides when the section is
// disabled or no enabled videos exist. YouTube plays via embed iframe;
// uploads play via a native <video> tag. Click-to-play — no autoplay so
// scroll performance and bandwidth stay clean.
// ---------------------------------------------------------------------------
function LandingVideosSection() {
  const [state, setState] = useState({ loading: true, enabled: true, videos: [], title: "", subtitle: "" });
  const [openVideo, setOpenVideo] = useState(null);

  useEffect(() => {
    api.get("/landing/videos")
      .then(({ data }) => setState({
        loading: false,
        enabled: !!data.enabled,
        videos: data.videos || [],
        title: data.section_title,
        subtitle: data.section_subtitle,
      }))
      .catch(() => setState((s) => ({ ...s, loading: false })));
  }, []);

  if (state.loading || !state.enabled || (state.videos || []).length === 0) return null;

  return (
    <section
      id="landing-videos"
      className="border-b-2 border-ink bg-page"
      data-testid="landing-videos-section"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="text-center mb-8">
          <p className="overline text-[10px] sm:text-[11px] mb-2 inline-flex items-center gap-1.5">
            <Play className="w-3 h-3" strokeWidth={3} /> Watch
          </p>
          <h2 className="font-display font-black text-2xl sm:text-3xl md:text-4xl tracking-tighter"
              data-testid="landing-videos-title">
            {state.title}
          </h2>
          <p className="text-sm sm:text-base text-ink/70 mt-2 max-w-xl mx-auto">
            {state.subtitle}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
             data-testid="landing-videos-grid">
          {state.videos.map((v) => (
            <button
              key={v.video_id}
              onClick={() => setOpenVideo(v)}
              data-testid={`landing-video-${v.video_id}`}
              className="group relative border-2 border-ink bg-ink text-cream shadow-brutalSm text-left overflow-hidden"
            >
              <div className="aspect-video bg-ink relative">
                {v.thumbnail_url ? (
                  <img
                    src={v.thumbnail_url}
                    alt={v.title || "Video preview"}
                    className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Play className="w-12 h-12" strokeWidth={2.5} />
                  </div>
                )}
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-14 h-14 rounded-full bg-white text-ink border-2 border-ink shadow-brutalSm flex items-center justify-center group-hover:scale-105 transition-transform">
                    <Play className="w-6 h-6 ml-0.5" strokeWidth={2.5} fill="currentColor" />
                  </span>
                </span>
              </div>
              {v.title && (
                <div className="px-3 py-2 bg-cream text-ink border-t-2 border-ink">
                  <p className="text-xs sm:text-sm font-bold truncate">{v.title}</p>
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {openVideo && (
        <div
          className="fixed inset-0 z-[90] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setOpenVideo(null)}
          data-testid="landing-video-modal"
        >
          <button
            className="absolute top-4 right-4 border-2 border-cream bg-ink text-cream p-2 shadow-brutalSm hover:bg-coral"
            onClick={() => setOpenVideo(null)}
            data-testid="landing-video-close"
            aria-label="Close video"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
          <div
            className="w-full max-w-4xl aspect-video border-2 border-cream bg-ink shadow-brutal"
            onClick={(e) => e.stopPropagation()}
          >
            {openVideo.kind === "youtube" && openVideo.embed_url ? (
              <iframe
                title={openVideo.title || "Landing video"}
                src={`${openVideo.embed_url}?autoplay=1&rel=0`}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video
                src={openVideo.embed_url || openVideo.url}
                controls
                autoPlay
                className="w-full h-full bg-black"
              />
            )}
          </div>
        </div>
      )}
    </section>
  );
}


// ---------------------------------------------------------------------------
// v146.41 — FloatingWhatsAppButton
// Persistent bottom-right green pill that opens wa.me/{phone}?text={msg}
// when the admin has configured support_whatsapp. Click event is logged
// via trackSiteEvent('landing_whatsapp_click') so super admin can see
// how many visitors reach out from the marketing site.
// ---------------------------------------------------------------------------
function FloatingWhatsAppButton() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/support-whatsapp")
      .then(({ data }) => setCfg(data && data.enabled ? data : null))
      .catch(() => setCfg(null));
  }, []);

  if (!cfg || !cfg.phone) return null;
  const href = `https://wa.me/${cfg.phone}${
    cfg.default_message ? `?text=${encodeURIComponent(cfg.default_message)}` : ""
  }`;
  const onClick = () => {
    trackSiteEvent("landing_whatsapp_click", {
      page_path: typeof window !== "undefined" ? window.location.pathname : null,
    });
  };
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      onClick={onClick}
      data-testid="landing-floating-whatsapp"
      aria-label={cfg.label || "Chat on WhatsApp"}
      className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 px-4 py-3 border-2 border-ink bg-emerald-500 text-white font-bold text-sm shadow-brutal hover:-translate-y-0.5 transition-transform"
    >
      <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
      <span className="hidden sm:inline">{cfg.label || "Support (WhatsApp)"}</span>
    </a>
  );
}




// v146.10 — AuthOrFallback + EmailPasswordDivider removed. Auth flow
// now lives on its own /signin page (`pages/AuthPage.jsx`). Those two
// helpers only existed to render the inline auth block on the hero.



/**
 * UrgencyBanner — sticky 1-liner at the very top of Landing that surfaces
 * the *best-active* plan offer (largest ₹ savings) with a live countdown.
 * Auto-hides when no plan has an active `offer_ends_at` in the future, so
 * the banner disappears the moment the sale ends — no maintenance.
 */
function UrgencyBanner({ plans, onCta }) {
  const [now, setNow] = useState(Date.now());
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("urgency_dismissed") === "1"; } catch { return false; }
  });

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const best = (plans || [])
    .filter((p) => p.active !== false && p.offer_ends_at && Number(p.mrp_inr) > Number(p.price_inr))
    .map((p) => ({ ...p, end: new Date(p.offer_ends_at).getTime() }))
    .filter((p) => !isNaN(p.end) && p.end > now)
    .sort((a, b) => (Number(b.mrp_inr) - Number(b.price_inr)) - (Number(a.mrp_inr) - Number(a.price_inr)))[0];

  if (dismissed || !best) return null;

  const secs = Math.floor((best.end - now) / 1000);
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const savings = Math.round(((best.mrp_inr - best.price_inr) / best.mrp_inr) * 100);
  const remain = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
  const dismiss = () => {
    try { sessionStorage.setItem("urgency_dismissed", "1"); } catch { /* noop */ }
    setDismissed(true);
  };

  return (
    <div
      className="bg-coral text-white border-b-2 border-ink pt-[env(safe-area-inset-top,0)]"
      data-testid="urgency-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-2 sm:gap-4 flex-wrap">
        <span className="text-lg" aria-hidden>⚡</span>
        <p className="text-xs sm:text-sm font-bold flex-1 min-w-0 truncate" data-testid="urgency-banner-copy">
          <span className="hidden sm:inline">{best.offer_label || `Launch pricing on ${best.name}`} — </span>
          Save {savings}% on <b>{best.name}</b>: <s className="opacity-75">₹{Number(best.mrp_inr).toLocaleString("en-IN")}</s>{" "}
          <b>₹{Number(best.price_inr).toLocaleString("en-IN")}</b>. Ends in <b>{remain}</b>.
        </p>
        <button
          onClick={onCta}
          data-testid="urgency-banner-cta"
          className="text-[11px] sm:text-xs font-black uppercase tracking-widest border-2 border-white bg-white text-coral px-3 py-1 hover:bg-ink hover:text-white hover:border-ink transition-colors"
        >
          Claim
        </button>
        <button
          onClick={dismiss}
          data-testid="urgency-banner-close"
          aria-label="Dismiss offer banner"
          className="text-white/80 hover:text-white text-lg leading-none font-bold px-1"
        >
          ×
        </button>
      </div>
    </div>
  );
}

function TrendingCard({ page }) {
  const initial = (page.name || "?").charAt(0).toUpperCase();
  return (
    <Link
      to={page.url}
      target="_blank"
      rel="noreferrer"
      onClick={externalLinkClickHandler(page.url, () =>
        trackSiteEvent("trending_click", { slug: page.slug, source: "landing" }))}
      className="group brutal-card p-0 overflow-hidden bg-white hover:-translate-y-0.5 transition-transform"
      data-testid={`trending-card-${page.slug}`}
    >
      <div className="h-24 sm:h-28 bg-gradient-to-br from-mint via-sun to-lavender relative border-b-2 border-ink">
        {page.cover_url && (
          <img src={page.cover_url} alt=""
               className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute -bottom-6 left-4 w-12 h-12 border-2 border-ink bg-white rounded-full overflow-hidden flex items-center justify-center font-display font-black text-lg">
          {page.avatar_url
            ? <img src={page.avatar_url} alt={page.name}
                   className="w-full h-full object-cover" />
            : initial}
        </div>
      </div>
      <div className="pt-8 px-4 pb-4">
        <p className="font-bold text-sm truncate" title={page.name}>{page.name}</p>
        <p className="text-[11px] text-ink/60 truncate">@{page.slug}</p>
        {page.tagline && (
          <p className="text-xs text-ink/70 mt-1.5 line-clamp-2">{page.tagline}</p>
        )}
        <div className="flex items-center gap-3 mt-3 text-[11px] font-bold">
          <span className="inline-flex items-center gap-1" data-testid={`trending-followers-${page.slug}`}>
            <Users className="w-3 h-3" strokeWidth={3} />
            {page.followers_total.toLocaleString("en-IN")}
          </span>
          <span className="inline-flex items-center gap-1 text-coral" data-testid={`trending-likes-${page.slug}`}>
            <Heart className="w-3 h-3 fill-current" strokeWidth={3} />
            {page.likes_total.toLocaleString("en-IN")}
          </span>
          {/* v146.36 — Show 30-day visits (same metric surfaced on the
              Trending page) so landing visitors can see traction at a
              glance without a click-through. */}
          {typeof page.views_30d === "number" && (
            <span className="inline-flex items-center gap-1 text-ink/60" data-testid={`trending-views-${page.slug}`}>
              <Eye className="w-3 h-3" strokeWidth={3} />
              {page.views_30d.toLocaleString("en-IN")}
            </span>
          )}
          <span className="ml-auto text-[10px] text-ink/50 uppercase group-hover:text-ink">
            visit →
          </span>
        </div>
      </div>
    </Link>
  );
}


// ---------------------------------------------------------------------------
// BIO PAGE EXAMPLES — v138.1 (Feb 2026)
// Four VISUALLY DISTINCT phone-frame mockups — each renders in a different
// real app theme so visitors see how the same product can dress up in
// radically different aesthetics:
//   * BRUTALIST  — sun/yellow bg, thick borders, hard black shadows (Fitness)
//   * HANDWRITTEN — cream bg, cursive font, dashed borders (Café)
//   * Y2K        — sky→lavender→pink gradient, Courier mono, chrome tiles (Fashion)
//   * EDITORIAL  — off-white, Times serif, hairline rules, red accent (Consultant)
// Each frame is a clickable link into the corresponding seeded demo hub
// (`/u/demo-*`) so visitors can experience the real /u/{slug} flow.
// ---------------------------------------------------------------------------
function PhoneFrame({ example }) {
  // v146.39 — Render the ACTUAL bio hub inside the phone frame via an
  // iframe pointed at /u/{slug}?embed=1&hide-overlay=1&hide-clone=1.
  // - `embed=1` + `hide-clone=1` suppress the demo-hub CTA ribbon.
  // - `hide-overlay=1` hides the Share / Join floating buttons.
  // - `pointer-events: none` on the iframe lets clicks fall through to
  //   the wrapping <Link>, so the whole phone stays a single tap target
  //   that opens the full page in a new tab. Users see the real theme,
  //   real blocks, and (unlike a screenshot) the design stays fresh as
  //   the demo hubs evolve.
  const src = `${example.href}?embed=1&hide-overlay=1&hide-clone=1`;
  return (
    <Link
      to={example.href}
      target="_blank"
      rel="noreferrer"
      onClick={externalLinkClickHandler(example.href)}
      data-testid={`bio-example-frame-${example.testid}`}
      className="group block relative w-full max-w-[280px] mx-auto hover:-translate-y-1 transition-transform"
      title={`Open ${example.name}'s live Web Link →`}
    >
      {/* Outer phone bezel */}
      <div className="border-[3px] border-ink rounded-[36px] p-2 bg-ink shadow-brutal">
        <div
          className="rounded-[28px] overflow-hidden border-2 border-ink relative bg-white"
          style={{ aspectRatio: "9 / 19" }}
        >
          <iframe
            src={src}
            title={`${example.name} Web Link preview`}
            loading="lazy"
            scrolling="no"
            className="absolute inset-0 w-full h-full border-0"
            style={{ pointerEvents: "none" }}
            data-testid={`bio-example-iframe-${example.testid}`}
          />
          {/* Fade at the bottom so any cut-off block edge looks intentional. */}
          <div
            className="absolute inset-x-0 bottom-0 h-16 pointer-events-none"
            style={{
              background: "linear-gradient(to bottom, rgba(255,255,255,0), rgba(0,0,0,0.06))",
            }}
          />
        </div>
      </div>
      {/* Notch */}
      <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-16 h-4 bg-ink rounded-b-xl" />
      {/* Hover overlay CTA */}
      <div className="absolute inset-0 flex items-end justify-center pb-6 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
        <span className="border-2 border-ink bg-coral text-white px-3 py-1 text-[11px] font-black uppercase tracking-wider shadow-brutalSm inline-flex items-center gap-1">
          See it live <ArrowUpRight className="w-3 h-3" strokeWidth={3} />
        </span>
      </div>
    </Link>
  );
}

// Theme presets used by PhoneFrame — mirror the real
// `frontend/src/styles/action-hub-themes.css` visual language.
const THEMES = {
  brutalist: {
    pageBg: "#fef8dc",
    fg: "#0f0f0f",
    statusBg: "#0f0f0f", statusFg: "#fef8dc",
    headerBg: "#FDE047",       // sun
    divider: "3px solid #0f0f0f",
    avatarBg: "#fff", avatarFg: "#0f0f0f", avatarRadius: 0,
    nameFg: "#0f0f0f", handleFg: "#0f0f0f", taglineFg: "#0f0f0f",
    titleFont: "'Cabinet Grotesk', ui-sans-serif, system-ui, sans-serif",
    titleWeight: 900, titleTracking: "-0.02em",
    bodyFont: "ui-sans-serif, system-ui, sans-serif",
    blockBg: "#fff", blockBorder: "3px solid #0f0f0f",
    blockRadius: 0, blockShadow: "4px 4px 0 0 #0f0f0f",
    chipBg: "#0f0f0f", chipFg: "#fff", chipBorder: "none",
  },
  handwritten: {
    pageBg: "#fdf6e3",
    fg: "#3b2f1e",
    statusBg: "#3b2f1e", statusFg: "#fdf6e3",
    headerBg: "#fff9e6",
    divider: "2px dashed #3b2f1e",
    avatarBg: "#d97706", avatarFg: "#fff", avatarRadius: "50%",
    nameFg: "#3b2f1e", handleFg: "#3b2f1e", taglineFg: "#3b2f1e",
    titleFont: "'Caveat', 'Comic Sans MS', cursive",
    titleWeight: 700, titleTracking: "0",
    bodyFont: "'Kalam', 'Comic Sans MS', cursive",
    blockBg: "#fff", blockBorder: "2px dashed #3b2f1e",
    blockRadius: "18px", blockShadow: "2px 2px 0 0 rgba(59,47,30,0.3)",
    chipBg: "#fef3c7", chipFg: "#3b2f1e", chipBorder: "1px solid #3b2f1e",
  },
  y2k: {
    pageBg: "linear-gradient(135deg,#7dd3fc 0%,#a78bfa 50%,#f472b6 100%)",
    fg: "#0b1226",
    statusBg: "rgba(11,18,38,0.9)", statusFg: "#fff",
    headerBg: "rgba(255,255,255,0.55)",
    divider: "2px solid #0b1226",
    avatarBg: "linear-gradient(135deg,#7dd3fc,#f472b6)", avatarFg: "#fff", avatarRadius: "50%",
    nameFg: "#0b1226", handleFg: "#0b1226", taglineFg: "#0b1226",
    titleFont: "'Courier New', 'JetBrains Mono', monospace",
    titleWeight: 700, titleTracking: "0",
    bodyFont: "'Courier New', monospace",
    blockBg: "linear-gradient(180deg,#ffffff,#dbeafe)",
    blockBorder: "2px solid #0b1226",
    blockRadius: "16px", blockShadow: "3px 3px 0 0 #db2777",
    chipBg: "#fff", chipFg: "#db2777", chipBorder: "2px solid #db2777",
  },
  editorial: {
    pageBg: "#fafaf7",
    fg: "#101010",
    statusBg: "#101010", statusFg: "#fafaf7",
    headerBg: "#fff",
    divider: "1px solid #101010",
    avatarBg: "#b91c1c", avatarFg: "#fff", avatarRadius: "50%",
    nameFg: "#101010", handleFg: "#101010", taglineFg: "#101010",
    titleFont: "'Times New Roman', Georgia, serif",
    titleWeight: 700, titleTracking: "-0.01em",
    bodyFont: "Georgia, 'Times New Roman', serif",
    blockBg: "#fff", blockBorder: "1px solid #101010",
    blockRadius: 0, blockShadow: "none",
    chipBg: "#fff", chipFg: "#b91c1c", chipBorder: "1px solid #b91c1c",
  },
};


/**
 * LivePreviewSection — v145.5
 *
 * A real, live-embedded demo Bio Page next to the hero so visitors see
 * the actual finished product before signing up. Uses same-origin
 * iframes pointing at `/u/{slug}` (X-Frame-Options is not set on our
 * demo hubs, verified via curl).
 *
 * Persona tabs let the visitor cycle through:
 *   - Ananya Rao   · Student  · Y2K
 *   - Kabir Sethi  · Gamer    · Neubrutalist
 *   - Zoya Ahmed   · Musician · Handwritten
 *   - Ishaan Sinha · Podcast  · Editorial
 *
 * Iframe is lazy-loaded and swaps on tab click. We track `trending_click`
 * whenever a visitor opens the full page in a new tab so the funnel view
 * captures preview engagement.
 */
function LivePreviewSection() {
  const PERSONAS = [
    { id: "student",   slug: "demo-ananya-rao",     name: "Ananya",  role: "Student",       theme: "Y2K",           color: "bg-gradient-to-r from-pink-300 to-fuchsia-300 text-ink",         emoji: "🎓" },
    { id: "gamer",     slug: "demo-kabir-sethi",    name: "Kabir",   role: "Gamer",         theme: "Neubrutalist",  color: "bg-gradient-to-r from-purple-400 to-indigo-500 text-white",     emoji: "🎮" },
    { id: "musician",  slug: "demo-zoya-ahmed",     name: "Zoya",    role: "Musician",      theme: "Handwritten",   color: "bg-gradient-to-r from-fuchsia-500 to-orange-400 text-white",     emoji: "🎤" },
    { id: "podcaster", slug: "demo-ishaan-podcast", name: "Ishaan",  role: "Podcaster",     theme: "Editorial",     color: "bg-sun text-ink",                                                emoji: "🎙️" },
  ];
  const [activeIdx, setActiveIdx] = useState(0);
  const [iframeReady, setIframeReady] = useState(false);
  const active = PERSONAS[activeIdx];

  const switchTo = (idx) => {
    if (idx === activeIdx) return;
    setIframeReady(false);
    setActiveIdx(idx);
  };

  return (
    <section
      className="border-b-2 border-ink bg-gradient-to-br from-page via-white to-bubble/30"
      data-testid="live-preview-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="text-center mb-8 sm:mb-10">
          <div className="inline-flex items-center gap-2 border-2 border-ink bg-white px-3 py-1 mb-4 shadow-brutalSm">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
            <span className="overline text-[10px]">LIVE PREVIEW · TAP ANYTHING</span>
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mb-3">
            See a real Web Link.<br className="sm:hidden" />{" "}
            <span className="bg-gradient-to-r from-purple-600 via-fuchsia-500 to-orange-400 bg-clip-text text-transparent">
              Interact with it.
            </span>
          </h2>
          <p className="text-ink/70 max-w-2xl mx-auto text-sm sm:text-base">
            This is an actual live Linkola page — the exact same thing you&apos;ll get in 60 seconds.
            Tap through the phone, hit the Spotify plays, open a link. Then flip between personas below.
          </p>
        </div>

        {/* Persona tabs */}
        <div
          className="flex flex-wrap items-center justify-center gap-2 mb-6 sm:mb-8"
          role="tablist"
          data-testid="live-preview-tabs"
        >
          {PERSONAS.map((p, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={p.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => switchTo(idx)}
                data-testid={`live-preview-tab-${p.id}`}
                className={`inline-flex items-center gap-2 border-2 border-ink px-3 py-1.5 text-xs sm:text-sm font-black uppercase tracking-widest transition-transform hover:-translate-y-0.5 ${
                  isActive ? `${p.color} shadow-brutal` : "bg-white text-ink/70 shadow-brutalSm"
                }`}
              >
                <span aria-hidden="true">{p.emoji}</span>
                {p.name} · {p.role}
              </button>
            );
          })}
        </div>

        {/* Phone shell + iframe. */}
        <div className="grid md:grid-cols-12 gap-6 items-center">
          <div className="hidden md:block md:col-span-3 space-y-3" data-testid="live-preview-left-notes">
            <div className="brutal-card bg-white p-4">
              <p className="overline text-[10px]">Now viewing</p>
              <p className="font-display font-black text-xl tracking-tighter mt-1">{active.name}</p>
              <p className="text-xs text-ink/60 mt-0.5">{active.role} · Theme · {active.theme}</p>
            </div>
            <div className="brutal-card bg-mint p-4">
              <p className="text-xs font-bold leading-snug">
                Every block you see here — Spotify, socials, videos, portfolio, links —
                is a drag-and-drop tile in your dashboard.
              </p>
            </div>
            <div className="brutal-card bg-lavender p-4">
              <p className="text-[11px] font-bold leading-snug">
                Change theme with one click. All 11 themes, all mobile-perfect.
              </p>
            </div>
          </div>

          <div className="md:col-span-6 flex justify-center">
            <div
              className="relative mx-auto bg-black border-[3px] border-ink shadow-brutal"
              style={{
                width: "min(92vw, 340px)",
                aspectRatio: "9 / 19",
                borderRadius: "44px",
                padding: "12px 8px",
              }}
              data-testid="live-preview-phone-shell"
            >
              {/* Notch / camera pill */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-black rounded-full flex items-center justify-center gap-1.5" style={{ width: "88px", height: "22px", zIndex: 2 }}>
                <span className="w-1.5 h-1.5 rounded-full bg-neutral-800" />
                <span className="w-3 h-3 rounded-full bg-neutral-900 border border-neutral-700" />
              </div>
              <div
                className="w-full h-full overflow-hidden bg-white relative"
                style={{ borderRadius: "34px" }}
              >
                {!iframeReady && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-[1]" data-testid="live-preview-loader">
                    <Loader2 className="w-6 h-6 animate-spin mb-2 text-ink/40" />
                    <p className="text-[11px] font-bold text-ink/50 uppercase tracking-widest">
                      Loading {active.name}&apos;s page…
                    </p>
                  </div>
                )}
                <iframe
                  key={active.slug}
                  src={`/u/${active.slug}?embed=1&hide-clone=1`}
                  title={`${active.name} · Live Linkola Web Link`}
                  className="w-full h-full border-0 block"
                  loading="lazy"
                  onLoad={() => setIframeReady(true)}
                  sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                  data-testid="live-preview-iframe"
                />
              </div>
              {/* Home indicator */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 h-1 rounded-full bg-neutral-700" style={{ width: "110px" }} />
            </div>
          </div>

          <div className="hidden md:block md:col-span-3 space-y-3" data-testid="live-preview-right-notes">
            <div className="brutal-card bg-sun p-4">
              <p className="overline text-[10px]">Built with</p>
              <p className="text-xs font-bold mt-1 leading-snug">
                Spotify embeds, YouTube, Instagram reels, portfolio links, resume PDFs, WhatsApp CTAs and 25+ more blocks.
              </p>
            </div>
            <a
              href={`/u/${active.slug}`}
              target="_blank"
              rel="noreferrer"
              onClick={externalLinkClickHandler(`/u/${active.slug}`, () =>
                trackSiteEvent("trending_click", { slug: active.slug, source: "live_preview" }))}
              className="brutal-card bg-white p-4 flex items-center justify-between hover:-translate-y-0.5 transition-transform"
              data-testid={`live-preview-open-${active.id}`}
            >
              <div>
                <p className="overline text-[10px]">Open full page</p>
                <p className="text-xs font-bold mt-0.5 truncate">/u/{active.slug}</p>
              </div>
              <ArrowUpRight className="w-4 h-4 shrink-0" strokeWidth={3} />
            </a>
            <div className="brutal-card bg-coral text-white p-4">
              <p className="text-xs font-bold leading-snug">
                Free forever. No credit card. Yours in 60 seconds.
              </p>
            </div>
          </div>
        </div>

        {/* Mobile-only compact CTA row */}
        <div className="md:hidden mt-6 flex flex-col items-center gap-3">
          <a
            href={`/u/${active.slug}`}
            target="_blank"
            rel="noreferrer"
            onClick={externalLinkClickHandler(`/u/${active.slug}`, () =>
              trackSiteEvent("trending_click", { slug: active.slug, source: "live_preview_mobile" }))}
            className="btn-secondary text-sm"
            data-testid={`live-preview-open-mobile-${active.id}`}
          >
            Open full page <ArrowUpRight className="w-4 h-4" />
          </a>
          <p className="text-[11px] text-ink/50 uppercase tracking-widest font-bold">
            free forever · no credit card
          </p>
        </div>
      </div>
    </section>
  );
}

function BioPageExamplesSection({ showPricing = true }) {
  // v146.74 — Dynamic examples pulled from `/api/landing/demo-hubs`.
  // Each demo carries a `category` matching the taxonomy in
  // `ExamplesModal.jsx` so users can filter by vertical (Doctor, Hotel,
  // Restaurant, Café, Fashion, Consultant, Podcaster, Musician,
  // Personal, Fitness). The rich phone-frame preview stays intact —
  // `PhoneFrame` iframes the live `/u/{slug}` so the real theme + real
  // blocks render inside the mockup without hardcoded block lists.

  // Display metadata (badge, colour, note, one-line title) is keyed by
  // slug. When a new demo is seeded on the backend without an entry
  // here, it falls back to a generic tone so the section still renders.
  const DEMO_META = {
    "demo-dr-mehra-clinic": {
      badge: "Doctor / Clinic", badgeTone: "bg-white text-ink",
      themeName: "Editorial",
      title: "Appointments · lab tests · directions",
      note: "Times serif, hairline rules. Books slots + collects patient reviews.",
    },
    "demo-taj-pearl-hotel": {
      badge: "Boutique hotel", badgeTone: "bg-lavender",
      themeName: "Editorial",
      title: "Rooms · reviews · rooftop dining",
      note: "Editorial serif, glossy gallery. Direct bookings, no middleman.",
    },
    "demo-bizil-kitchen": {
      badge: "Restaurant", badgeTone: "bg-coral text-white",
      themeName: "Handwritten",
      title: "Menu · reservations · reviews",
      note: "Warm handwritten script. Turns a dinner into a regular.",
    },
    "demo-brew-bite": {
      badge: "Small café", badgeTone: "bg-cream",
      themeName: "Handwritten",
      title: "Coupons · maps · reviews",
      note: "Cursive script, dashed borders. Turns walk-ins into regulars.",
    },
    "demo-maya-fit": {
      badge: "Fitness coach", badgeTone: "bg-mint",
      themeName: "Brutalist",
      title: "Book · sell · convert",
      note: "Chunky borders, hard shadows. Books 3× more clients per week.",
    },
    "demo-simran-kohli": {
      badge: "Fashion creator", badgeTone: "bg-sun",
      themeName: "Y2K",
      title: "Reels · shop · attribute",
      note: "Sky→pink gradient, monospace type. Every reel is a landing page.",
    },
    "demo-raj-menon": {
      badge: "Consultant", badgeTone: "bg-lavender",
      themeName: "Editorial",
      title: "Lead form · calendar · social proof",
      note: "Times serif, hairline rules. Captures qualified leads, books calls.",
    },
    "demo-ananya-rao": {
      badge: "College student", badgeTone: "bg-gradient-to-r from-pink-300 to-fuchsia-300",
      themeName: "Y2K",
      title: "Playlist · portfolio · vlog",
      note: "Pastel gradients, monospace type. Design student sharing her whole vibe.",
    },
    "demo-kabir-sethi": {
      badge: "School / gamer", badgeTone: "bg-gradient-to-r from-purple-400 to-indigo-500 text-white",
      themeName: "Neubrutalist",
      title: "Playlist · clips · Discord",
      note: "Big blocks, playful shadows. Class 12 gamer curating his socials.",
    },
    "demo-ishaan-podcast": {
      badge: "Podcaster", badgeTone: "bg-sun",
      themeName: "Editorial",
      title: "Show · episodes · newsletter",
      note: "Times serif, hairline rules. Weekly show + fan mailing list.",
    },
    "demo-zoya-ahmed": {
      badge: "Musician", badgeTone: "bg-gradient-to-r from-fuchsia-500 to-orange-400 text-white",
      themeName: "Handwritten",
      title: "Album · music video · bookings",
      note: "Cursive script, warm tones. New EP + gig bookings all in one link.",
    },
  };

  // Taxonomy mirrors ExamplesModal.jsx so both surfaces stay in sync.
  const CATEGORY_META = [
    { key: "all",         label: "All categories" },
    { key: "doctor",      label: "Doctor / Clinic" },
    { key: "hotel",       label: "Hotel" },
    { key: "restaurant",  label: "Restaurant" },
    { key: "cafe",        label: "Café / Bakery" },
    { key: "fitness",     label: "Fitness / Coach" },
    { key: "fashion",     label: "Fashion / Boutique" },
    { key: "consultant",  label: "Consultant" },
    { key: "podcast",     label: "Podcaster" },
    { key: "musician",    label: "Musician / Artist" },
    { key: "personal",    label: "Personal / Creator" },
  ];

  const [demos, setDemos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    let alive = true;
    api.get("/landing/demo-hubs")
      .then(({ data }) => { if (alive) setDemos(Array.isArray(data?.demos) ? data.demos : []); })
      .catch(() => { if (alive) setDemos([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Only show category options that actually have demos.
  const availableCategories = useMemo(() => {
    if (!demos) return CATEGORY_META.slice(0, 1);
    const present = new Set(demos.map((d) => d.category || "personal"));
    return CATEGORY_META.filter((c) => c.key === "all" || present.has(c.key));
  }, [demos]);

  // Merge API rows with local display metadata.
  const enriched = useMemo(() => {
    if (!demos) return [];
    return demos.map((d) => {
      const meta = DEMO_META[d.slug] || {
        badge: (d.category || "personal").replace(/^./, (m) => m.toUpperCase()),
        badgeTone: "bg-cream text-ink",
        themeName: (d.theme || "classic").replace(/^./, (m) => m.toUpperCase()),
        title: `${d.block_count} blocks · ${d.theme || "classic"} theme`,
        note: d.bio || "Live demo hub — tap to explore.",
      };
      return {
        ...meta,
        testid: d.slug,
        slug: d.slug,
        href: `/u/${d.slug}`,
        name: d.name,
        category: d.category || "personal",
      };
    });
  }, [demos]);

  const filtered = useMemo(() => {
    if (category === "all") return enriched;
    return enriched.filter((d) => d.category === category);
  }, [enriched, category]);

  return (
    <section id="bio-examples" className="border-b-2 border-ink bg-page" data-testid="bio-examples-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="inline-flex items-center gap-2 border-2 border-ink bg-lavender px-3 py-1 mb-4 shadow-brutalSm">
          <Palette className="w-4 h-4" strokeWidth={3} />
          <span className="overline">Web Link examples · tap to open live</span>
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mb-3">
          One link. Every kind of business &amp; person.
        </h2>
        <p className="text-ink/70 max-w-2xl mb-6 text-base">
          Every hub gets a real theme — Brutalist, Handwritten, Y2K, Editorial,
          Neubrutalist and more. Plug in your Spotify playlist, portfolio,
          products, socials, videos, coupons or lead form — mix and match{" "}
          <span className="font-bold">30+ blocks</span>.{" "}
          <span className="font-bold">Filter by category, then tap any phone to open the live demo.</span>
        </p>

        {/* v146.74 — Category filter dropdown */}
        <div
          className="mb-8 flex items-center gap-3 flex-wrap border-2 border-ink bg-white px-3 py-2 shadow-brutalSm max-w-md"
          data-testid="bio-examples-filter-row"
        >
          <Filter className="w-4 h-4 shrink-0" strokeWidth={3} />
          <label htmlFor="bio-examples-category" className="text-[11px] font-black uppercase tracking-widest shrink-0">
            Category
          </label>
          <select
            id="bio-examples-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="bio-examples-category-select"
            className="flex-1 min-w-0 border-2 border-ink bg-cream px-2 py-1.5 text-xs font-bold shadow-brutalSm cursor-pointer"
          >
            {availableCategories.map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
          <span className="text-[11px] font-mono text-ink/60 shrink-0" data-testid="bio-examples-count">
            {filtered.length} / {enriched.length}
          </span>
        </div>

        {loading && (
          <div className="py-12 text-center text-ink/60 text-sm" data-testid="bio-examples-loading">
            <Loader2 className="w-5 h-5 mx-auto mb-2 animate-spin" />
            Loading examples…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="py-12 text-center text-ink/60 text-sm" data-testid="bio-examples-empty">
            No examples in this category yet. Pick another.
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <div
            className="grid gap-8 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4"
            data-testid="bio-examples-grid"
          >
            {filtered.map((ex) => (
              <div key={ex.testid} className="flex flex-col items-center text-center" data-testid={`bio-example-${ex.testid}`}>
                <div className={`inline-flex items-center gap-1 border-2 border-ink px-2.5 py-1 mb-2 shadow-brutalSm text-[11px] font-black uppercase tracking-widest ${ex.badgeTone}`}>
                  {ex.badge}
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest text-ink/50 mb-3">
                  Theme · {ex.themeName}
                </span>
                <PhoneFrame example={ex} />
                <p className="font-bold text-sm mt-4">{ex.title}</p>
                <p className="text-xs text-ink/60 mt-1 max-w-[240px]">{ex.note}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 border-2 border-dashed border-ink/30 bg-white px-4 py-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-ink/70">
            <span className="font-bold">30+ blocks · 11 themes.</span>{" "}
            Spotify, Products, Services, Storefront, Coupons, Video Reels,
            Booking, Lead Form, Testimonials, Google Reviews, WhatsApp, Maps,
            FAQs, Countdown, Newsletter, Featured product, and more.
          </p>
          {showPricing && (
            <a href="#pricing" className="btn-secondary text-sm" data-testid="bio-examples-cta">
              See pricing <ArrowUpRight className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// v146.77 — VerticalStripSection
// A compact 10-tile strip that maps every business vertical the platform
// supports onto its dedicated `/for/{vertical}` landing page. Sits right
// below the bio-hub examples so visitors browsing "who's this for?"
// naturally discover the SEO pages.
// ---------------------------------------------------------------------------
function VerticalStripSection() {
  const tiles = [
    { key: "doctors",     icon: Stethoscope, label: "Doctors",     tone: "bg-white text-ink" },
    { key: "hotels",      icon: BedDouble,   label: "Hotels",      tone: "bg-ink text-white" },
    { key: "restaurants", icon: Utensils,    label: "Restaurants", tone: "bg-coral text-white" },
    { key: "cafes",       icon: Coffee,      label: "Cafés",       tone: "bg-cream" },
    { key: "fitness",     icon: Dumbbell,    label: "Fitness",     tone: "bg-sun" },
    { key: "fashion",     icon: Shirt,       label: "Fashion",     tone: "bg-lavender" },
    { key: "consultants", icon: Briefcase,   label: "Consultants", tone: "bg-white" },
    { key: "podcasters",  icon: Mic,         label: "Podcasters",  tone: "bg-mint" },
    { key: "musicians",   icon: Music,       label: "Musicians",   tone: "bg-gradient-to-r from-fuchsia-500 to-orange-400 text-white" },
    { key: "personal",    icon: User,        label: "Personal",    tone: "bg-gradient-to-r from-pink-300 to-fuchsia-300" },
  ];
  return (
    <section id="verticals" className="border-b-2 border-ink bg-white" data-testid="verticals-strip-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="inline-flex items-center gap-2 border-2 border-ink bg-mint px-3 py-1 mb-4 shadow-brutalSm">
          <Sparkles className="w-4 h-4" strokeWidth={3} />
          <span className="overline">Built for your business</span>
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
            One-page pitch. Every vertical.
          </h2>
          <p className="text-sm text-ink/60 max-w-md">
            A landing page tuned for how each business actually sells — with the right blocks, the right testimonials, and a live demo hub inside a phone frame.
          </p>
        </div>
        <div
          className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-5"
          data-testid="verticals-strip-grid"
        >
          {tiles.map((t) => (
            <Link
              key={t.key}
              to={`/for/${t.key}`}
              className={`group relative border-2 border-ink ${t.tone} p-4 shadow-brutalSm hover:-translate-y-1 hover:shadow-brutal transition-all flex flex-col items-start gap-2`}
              data-testid={`verticals-strip-tile-${t.key}`}
            >
              <div className={`w-9 h-9 border-2 border-ink flex items-center justify-center ${t.tone.includes("bg-ink") || t.tone.includes("text-white") ? "bg-white/20 text-white" : "bg-white text-ink"}`}>
                <t.icon className="w-4 h-4" strokeWidth={2.5} />
              </div>
              <span className="font-black text-sm tracking-tight uppercase">{t.label}</span>
              <span className="text-[10px] font-mono opacity-70 lowercase">/for/{t.key}</span>
              <ArrowUpRight className="w-4 h-4 absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={3} />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// Tiny fallback icon for the "All" option so the dropdown renders even
// before lucide chunks load.



// ---------------------------------------------------------------------------
// INFLUENCER FLOW — v138 (Feb 2026)
// Horizontal 4-step diagram: Discover → Apply → Promote → Earn.
// Step cards are joined by arrow chevrons on lg+; stack vertically on mobile.
// ---------------------------------------------------------------------------
function FlowStep({ n, icon: Icon, tone, title, desc, chips, testid }) {
  return (
    <div className={`relative brutal-card p-5 flex flex-col text-ink ${tone}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="font-display font-black text-4xl tracking-tighter opacity-90 leading-none">{n}</span>
        <div className="w-10 h-10 border-2 border-ink bg-white flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-ink" strokeWidth={2.5} />
        </div>
      </div>
      <h3 className="font-display font-black text-lg tracking-tighter mb-1">{title}</h3>
      <p className="text-[13px] leading-snug opacity-90">{desc}</p>
      {chips && chips.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {chips.map((c) => (
            <span key={c} className="text-[10px] font-bold border border-ink bg-white/80 text-ink px-1.5 py-0.5">
              {c}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function FlowArrow() {
  return (
    <div className="hidden lg:flex items-center justify-center px-1" aria-hidden>
      <div className="relative w-full">
        <div className="h-1 bg-ink w-full" />
        <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-0 h-0 border-y-[7px] border-y-transparent border-l-[10px] border-l-ink" />
      </div>
    </div>
  );
}

function InfluencerFlowSection() {
  const steps = [
    { n: "01", icon: Search,     tone: "bg-mint",           title: "Discover campaigns",
      desc: "Browse the marketplace — filter by category, city, budget, and payout type. See exactly what each brand expects (a reel, a story, a review) before you apply.",
      chips: ["Fashion", "Food", "Local"], testid: "inf-flow-step-1" },
    { n: "02", icon: Handshake,  tone: "bg-sun",            title: "Apply · get accepted",
      desc: "One-tap apply with your rate card. Brands see your reach, engagement and past deliveries. Accept the counter-offer or negotiate directly in-app.",
      chips: ["Rate card", "In-app chat"], testid: "inf-flow-step-2" },
    { n: "03", icon: Megaphone,  tone: "bg-lavender",       title: "Promote your way",
      desc: "Drop the brand's Action Page onto your Web Link, share the smart link in your reel, story or DM. Every tap, coupon unlock and sale is auto-attributed to you.",
      chips: ["Smart links", "Coupons", "Reels"], testid: "inf-flow-step-3" },
    { n: "04", icon: Wallet,     tone: "bg-coral !text-white", title: "Earn instantly",
      desc: "Get paid the moment the outcome is verified — no invoices, no chasing. Withdraw to UPI or bank from your Linkola Wallet in under 60 seconds.",
      chips: ["Instant UPI", "0 platform lock-in"], testid: "inf-flow-step-4" },
  ];
  return (
    <section id="influencer-flow" className="border-b-2 border-ink bg-cream" data-testid="influencer-flow-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="inline-flex items-center gap-2 border-2 border-ink bg-mint px-3 py-1 mb-4 shadow-brutalSm">
          <UserCheck className="w-4 h-4" strokeWidth={3} />
          <span className="overline">For creators &amp; influencers</span>
        </div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter max-w-3xl">
            Pick a campaign. Promote. Get paid.
          </h2>
          <a href="/marketplace" className="btn-secondary" data-testid="influencer-flow-cta">
            Browse the marketplace <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
        <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr]">
          <FlowStep {...steps[0]} />
          <FlowArrow />
          <FlowStep {...steps[1]} />
          <FlowArrow />
          <FlowStep {...steps[2]} />
          <FlowArrow />
          <FlowStep {...steps[3]} />
        </div>
        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          {[
            { icon: BarChart3, k: "Real-time analytics", v: "Every tap, purchase, coupon unlock — attributed automatically." },
            { icon: Ticket,    k: "Coupon marketplace",  v: "Sell paid coupons for local shops. Earn on every unlock + redemption." },
            { icon: Handshake, k: "Affiliate picks",     v: "Resell any Brand-plan creator's product. Earn on every sale." },
          ].map((f) => (
            <div key={f.k} className="border-2 border-ink bg-white p-4 flex items-start gap-3 shadow-brutalSm">
              <div className="w-9 h-9 border-2 border-ink bg-mint flex items-center justify-center flex-shrink-0">
                <f.icon className="w-4 h-4" strokeWidth={2.5} />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-sm">{f.k}</p>
                <p className="text-xs text-ink/70 mt-0.5">{f.v}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


// ---------------------------------------------------------------------------
// BRAND FLOW — v138 (Feb 2026)
// Horizontal 5-step diagram: Post → Discover → Invite → Track → Pay.
// ---------------------------------------------------------------------------
function BrandFlowSection() {
  const steps = [
    { n: "01", icon: Megaphone,   tone: "bg-white",           title: "Post a campaign",
      desc: "Describe what you need — a reel, a review, a coupon push. Set the target (city, category, follower band) and outcome-linked payout.",
      chips: ["City targeting", "Outcome-linked"], testid: "brand-flow-step-1" },
    { n: "02", icon: Search,       tone: "bg-sun",             title: "Discover influencers",
      desc: "Browse creators by niche, city, engagement rate, and past-campaign delivery. See real Instagram/YouTube stats before you spend a rupee.",
      chips: ["Real stats", "Delivery track record"], testid: "brand-flow-step-2" },
    { n: "03", icon: MessageSquare, tone: "bg-lavender",        title: "Contact · negotiate",
      desc: "Send a personal invite, negotiate the scope + rate in-app, lock the deal with an escrowed wallet deposit. No off-platform ghosting.",
      chips: ["In-app chat", "Escrow"], testid: "brand-flow-step-3" },
    { n: "04", icon: BarChart3,    tone: "bg-mint",             title: "Track performance",
      desc: "Watch clicks, conversions, coupon unlocks and sales come in live. Every touchpoint is attributed to the influencer that drove it.",
      chips: ["Live funnel", "Attribution"], testid: "brand-flow-step-4" },
    { n: "05", icon: DollarSign,   tone: "bg-coral !text-white", title: "Pay for outcomes",
      desc: "Approve the delivery and release the payout — from the escrowed wallet — automatically. Or set a CPS/CPC model so you only pay for what actually converted.",
      chips: ["CPS · CPC · CPM", "0 chargebacks"], testid: "brand-flow-step-5" },
  ];
  return (
    <section id="brand-flow" className="border-b-2 border-ink bg-ink text-cream" data-testid="brand-flow-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20">
        <div className="inline-flex items-center gap-2 border-2 border-cream bg-coral text-white px-3 py-1 mb-4 shadow-brutalSm">
          <Target className="w-4 h-4" strokeWidth={3} />
          <span className="overline">For brands</span>
        </div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter max-w-3xl text-cream">
            Find real influencers. Pay only for real outcomes.
          </h2>
          <a href="/marketplace" className="btn-secondary bg-cream text-ink" data-testid="brand-flow-cta">
            Post your first campaign <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
        <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr_auto_1fr]">
          <FlowStep {...steps[0]} />
          <FlowArrow />
          <FlowStep {...steps[1]} />
          <FlowArrow />
          <FlowStep {...steps[2]} />
          <FlowArrow />
          <FlowStep {...steps[3]} />
          <FlowArrow />
          <FlowStep {...steps[4]} />
        </div>
        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          {[
            { icon: MapPin,     k: "Hyper-local targeting", v: "Filter down to a specific city, neighbourhood or pincode." },
            { icon: CheckCircle2, k: "Escrow protection",   v: "Funds sit in your Linkola Wallet until you approve delivery." },
            { icon: LineChart,  k: "ROI at a glance",       v: "Every campaign has a live dashboard — clicks, sales, coupons, revenue." },
          ].map((f) => (
            <div key={f.k} className="border-2 border-cream bg-ink/40 p-4 flex items-start gap-3">
              <div className="w-9 h-9 border-2 border-cream bg-coral text-white flex items-center justify-center flex-shrink-0">
                <f.icon className="w-4 h-4" strokeWidth={2.5} />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-sm text-cream">{f.k}</p>
                <p className="text-xs text-cream/70 mt-0.5">{f.v}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


